import App from 'app/app.js';
import ValidationException from 'app/store/ValidationException.js';
import { hasRequiredSubjectDetails } from 'app/service/student-detail/subject-completeness';
import 'app/store/ProductCatalogStore.js';
import 'app/store/JobStore.js';
import 'app/store/UserProfileStore.js';
import UpgradeType from 'app/store/UpgradeType.js';
import 'app/action/JobLoadedAction.js';
import 'app/action/DeserializeAction.js';
import 'app/action/SessionTimeoutAction.js';
import 'app/service/OrderLineFactory.js';
import 'app/service/StorageService.js';
import 'app/service/TranslateProviderService.js';
import 'app/action/LogoutSuccessAction.js';
import 'app/action/ResetJobAction.js';
import 'app/action/EmptyCartAction.js';
import 'app/service/GuidGenerator.js';
import 'app/store/ContentStore.js';
import 'app/service/AnalyticsService.js';
import 'app/components/payment/button/googlepay/google-pay';
import 'app/components/payment/button/applepay/apple-pay-button.component';
import 'app/components/payment/button/venmo/venmo';
import 'app/components/payment/button/paypal/paypal';
import 'app/components/payment/button/creditcard/credit-card';
import { roundToDecimals } from '@app/utils/math';
import { isAutoApplyCoupon } from '@app/utils/coupon.utils';
import { AutoApplyCouponType } from '@app/interface/content/auto-apply-coupon-type.enum';
import { partitionArray, setsIntersect } from '@app/utils/collection.utils';

// eslint-disable-next-line max-lines-per-function
function ShoppingCartStore(
  $timeout,
  ProductCatalogStore,
  JobStore,
  UserProfileStore,
  LogoutSuccessAction,
  JobLoadedAction,
  DeserializeAction,
  SessionTimeoutAction,
  ResetJobAction,
  EmptyCartAction,
  OrderLineFactory,
  StorageService,
  TranslateProviderService,
  $http,
  $q,
  ShoppingCartConstants,
  GuidGenerator,
  ContentStore,
  AnalyticsService,
  $uibModal,
  ErrorLogService,
  StandardizedEnhancementService,
  SubjectStore,
  FlightRecorderService,
  PersistentCartService,
  ShippingTypeService,
  EnhancementStore,
  $rootScope,
  $injector,
  loggerService,
  FeatureFlagService,
  DigitalDownloadService,
  LooksStore
) {
  let orderIdsHistory = [];
  let subjects = [];
  let coupons = [];
  let activeSubject = null;
  const getStateProvince = function () {
    let stateProv;
    if (!!this.shippingAddress && !!this.shippingAddress.stateProvince) {
      stateProv = this.shippingAddress.stateProvince;
      if (!stateProv.country) {
        stateProv.country = this.shippingAddress.country;
      }
    } else if (!!this.payment && !!this.payment.billingAddress && !!this.payment.billingAddress.stateProvince) {
      stateProv = this.payment.billingAddress.stateProvince;
    } else {
      stateProv = { name: JobStore.getJob().jobState, country: JobStore.getJob().jobCountry };
    }
    return stateProv;
  }.bind(this);
  this.payment = {};
  this.clientAuthorization = {};
  this.shippingAddress = {};
  this.suggestedAddress = {};
  this.payOnlySalesPrice = 0;
  this.fees = [];
  const that = this;
  this.checkForLoyaltyUser = false;
  this.paymentTimedOut = false;
  this.paymentVoucherIds = [];
  this.totalAmount = 0;
  this.familyDiscountActivated = false;
  this.savedStudentDetailOrderIds = new Set();
  // Monotonic counter bumped whenever adjustPricing completes a reprice. Watchers can
  // key off this to react only after prices/flags (e.g. shippingAddressRequired) settle.
  this.repriceVersion = 0;

  function validateItem(type, item) {
    if (item === null || item === undefined) {
      throw `${type} cannot be null or undefined`;
    }
  }

  function wrapOrderLines(orderLines, jobKey) {
    return orderLines.map((orderLineData) =>
      OrderLineFactory.createFromData(orderLineData, { looksStore: LooksStore, jobKey })
    );
  }

  function wrapOrder(orderData, jobKey) {
    orderData.packages = wrapOrderLines(orderData.packages, jobKey);
    orderData.addOns = wrapOrderLines(orderData.addOns, jobKey);
    orderData.bundles = wrapOrderLines(orderData.bundles, jobKey);
    orderData.upgrades = wrapOrderLines(orderData.upgrades, jobKey);

    if (orderData.availableShippingMethods) {
      orderData.availableShippingMethods = wrapOrderLines(orderData.availableShippingMethods, jobKey);
    }

    return OrderLineFactory.createOrder(orderData);
  }

  function extractName(name) {
    const retVal = { firstName: '', lastName: '' };
    const names = !!name ? name.split(' ') : [];

    if (names.length) {
      if (names.length > 1) {
        retVal.firstName = names.splice(0, 1)[0];
        retVal.lastName = names.join(' ');
      } else {
        retVal.firstName = name;
        retVal.lastName = '';
      }
    }

    return retVal;
  }

  this.getSubjectByJobNumber = (jobNumber) => subjects?.find((subject) => subject.jobContext.jobNumber === jobNumber);

  this.getSubjects = function (jobNumber) {
    if (!jobNumber) {
      return subjects;
    }

    const isEqual = R.pathEq(ShoppingCartConstants.subjectJobNumberMap, jobNumber);
    return R.filter(isEqual, subjects);
  };

  this.getCoupons = function () {
    return !R.isEmpty(subjects) ? coupons : [];
  };

  this.getNumberOfSubjects = function () {
    return subjects.length;
  };

  this.setClientAuthorization = function (clientAuthorization = {}) {
    this.clientAuthorization = clientAuthorization;
    this.updateStorage();
  };

  this.getClientAuthorization = function () {
    return this.clientAuthorization;
  };

  this.isFamilyDiscountActivated = () => this.familyDiscountActivated;

  this.setFamilyDiscountActivated = (value) => (this.familyDiscountActivated = value);

  this.hasPackagesInAllSubjects = () => this.getSubjects().every((sub) => sub.packages.length > 0);

  this.hasDiscountByJobNumber = () => (jobNumber) =>
    this.getSubjects().find((subject) => subject.jobContext.jobNumber === jobNumber && subject.packages.length > 0);

  this.changeCurrentSubject = (subject) => {
    const orderIndex = this.getSubjectIndex(subject.id);

    this.setCurrentSubject(orderIndex);
  };

  this.setCurrentSubject = function (orderIndex) {
    FlightRecorderService.log(`ShoppingCartStore:setCurrentSubject:${orderIndex}`);
    //If the order index is greater than the array size it breaks the program. Adding this as protection.
    if (orderIndex > subjects.length) {
      orderIndex = 0;
    }
    activeSubject = subjects[orderIndex];
    JobStore.setActiveJob(activeSubject.portraitDashboard.jobKey);
    SubjectStore.studentBannerSyncupWithCart(JobStore.getJob(), activeSubject);
    AnalyticsService.updateContentView();
    this.updateStorage();
    this.storeOrderIdsHistory();
  };

  this.storeOrderIdsHistory = function () {
    const current = this.getCurrentSubject().id;
    this.removeFromOrderIdsHistory(current);
    orderIdsHistory.push(current);
  };

  this.removeFromOrderIdsHistory = function (subjectId) {
    orderIdsHistory = orderIdsHistory?.filter((id) => id !== subjectId) || [];
  };

  this.getOrderIdsHistory = function () {
    return orderIdsHistory;
  };

  this.getSubjectOrder = function (orderIndex) {
    const currentSubject = orderIndex >= 0 ? subjects[orderIndex] : this.getCurrentSubject();
    if (!currentSubject) {
      return this.getCurrentSubject();
    }
    return currentSubject;
  };

  this.getAllSubjects = function () {
    return subjects;
  };
  this.canAddCoupons = function () {
    return ContentStore.getConfig().additionalCouponsLimit + subjects.length > this.getRegularCouponCount();
  };

  this.getSubjectIndex = function (id) {
    return R.findIndex((subject) => subject.id === id)(subjects);
  };

  this.getSubjectById = function (id) {
    if (!id) return undefined;
    return R.find(function (subject) {
      return subject.id === id;
    })(subjects);
  };

  this.isSkuAssociatedWithSubjectThatHasPackages = (skuCode) => {
    if (!skuCode) return false;
    return (this.getAllSubjects() || []).some((subject) => {
      const collections = [subject.addOns, subject.bundles];
      const skuExists = collections.some((collection) =>
        (collection || []).some((item) => item?.orderable?.skuCode === skuCode)
      );
      return skuExists && subject.packages?.length > 0;
    });
  };

  this.couponApplied = function (couponCode) {
    return R.find(R.propEq('couponCode', couponCode))(coupons);
  };

  this.applyCoupon = function (enteredCouponCode) {
    const couponCode = enteredCouponCode.toUpperCase();
    const jobContext = JobStore.getJob();
    let results = {};
    const sameCouponCode = this.couponApplied(couponCode);

    if (!this.canAddCoupons()) {
      return $q.when({
        warningMessage: 'COUPON_LIMIT_EXCEEDED'
      });
    }

    if (sameCouponCode) {
      return $q.when({
        warningMessage: 'COUPON_DUPLICATE_ERROR',
        messageParams: { couponCode }
      });
    }

    if (R.isEmpty(this.buildSubjectListForSubmission(false))) {
      return $q.when({
        warningMessage: 'COUPON_PAYONLY_MESSAGE',
        messageParams: { couponCode }
      });
    }

    // call a coupon service
    return $http
      .post('cart/validateCoupon', {
        orders: this.buildSubjectListForSubmission(false),
        currentCouponCode: couponCode,
        coupons, //Any existing Coupons
        shippingAddress: this.shippingAddress,
        context: { applyFamilyDiscountCoupon: this.familyDiscountActivated }
      })
      .then((response) => {
        if (response.data.success) {
          const couponApplied = !!R.find(R.propEq('couponCode', couponCode))(response.data.data.coupons);

          if (!couponApplied) {
            results = {
              messageKeys: [`${couponCode.substring(0, 4)}_ERROR`, 'COUPON_NOT_APPLIED'], // MLC-3044
              warningMessage: 'COUPON_NOT_APPLIED',
              messageParams: { couponCode }
            };
          } else {
            // update current subject
            //No change in subjectList is required for adjustPricing call from 'applyCoupon' method. Hence orderIndex is sent as null.
            this.adjustPricing(response.data.data.orders, null, response.data.data.coupons);
            const message = 'COUPON_SUCCESS_MESSAGE';
            AnalyticsService.updateContentView();
            AnalyticsService.recordEvent(AnalyticsService.events.CART_REPRICE);
            results = { successMessage: message, messageParams: { couponCode } };
            AnalyticsService.setCouponCode(couponCode);
            AnalyticsService.recordEvent(AnalyticsService.events.COUPON_CODE_APPLIED);
          }
        } else {
          results = {
            warningMessage: response.data.error,
            messageParams: { couponCode }
          };
        }
      })
      .catch((response) => {
        if (response.data && response.data.error && response.data.error.code === 'EXISTING_COUPONS_NOT_STACKABLE') {
          results = {
            warningMessage: response.data.error.code,
            messageParams: { couponCode: coupons[0].couponCode }
          };
        } else {
          results = {
            warningMessage: response.data.error ? response.data.error.code : 'COMMON_TEXT_ERROR',
            messageParams: { couponCode }
          };
        }
      })
      .then(
        () => results,
        () => results
      );
  };

  this.removeCoupon = function (couponCode) {
    if (!!coupons && !R.isEmpty(coupons)) {
      const couponIndex = R.findIndex(R.propEq('couponCode', couponCode))(coupons);
      const coupon = coupons[couponIndex];
      if (coupon?.autoApplyType === AutoApplyCouponType.FAMILY_DISCOUNT) {
        this.familyDiscountActivated = false;
      }
      coupons.splice(couponIndex, 1);
    }
    this.repriceCart();
  };

  /**
   * Sets copy-order change flags on a subject so ShoppingCartCtrl can render
   * per-subject lt-alert banners. Call after repriceCart() resolves and the
   * source-vs-target comparison is complete. Flags persist until the user
   * dismisses each alert via clearSubjectAlert().
   */
  this.setSubjectFlags = function (subjectId, flags) {
    const subject = subjects.find((s) => s.id === subjectId);
    if (!subject) return;
    subject.hasPricingUpdate = flags.hasPricingUpdate;
    subject.hasUnavailableSelections = flags.hasUnavailableSelections;
    subject.hasBackgroundUpdate = flags.hasBackgroundUpdate;
  };

  this.adjustPricing = function (updatedOrders, orderIndex, updatedCoupons) {
    let subjectList = [];

    if (!!orderIndex && orderIndex >= 0) {
      subjectList.push(this.getSubjectOrder(orderIndex));
    } else {
      subjectList = subjects;
    }

    subjectList.forEach((subject) => {
      let updatedCart = updatedOrders.find((order) => order.id === subject.id);
      let updateOrderLines = (currentOrderLines, updatedOrderLines) => {
        updatedOrderLines.forEach((updatedOrderLine, idx) => {
          if (!currentOrderLines[idx]) {
            loggerService.warn('ShoppingCartStore.updateOrderLines: index out of bounds', {
              idx,
              currentLength: currentOrderLines.length,
              updatedLength: updatedOrderLines.length
            });
            return;
          }
          currentOrderLines[idx].salesPrice = updatedOrderLine.salesPrice;
          currentOrderLines[idx].orderPromotion = updatedOrderLine.orderPromotion;
          if (JobStore.isEnhancedPayOnly(subject.portraitDashboard.jobKey)) {
            //Whenever the items are removed from enhanced payonly package the sales price changes.
            //Update unit price so that family approval page and shopping cart page will match.
            currentOrderLines[idx].orderable.unitPrice = updatedOrderLine.salesPrice;
          }
          // set the shipping location on each product
          currentOrderLines[idx].shipToLocation = updatedOrderLine.shipToLocation;
        });
      };

      if (subject && !updatedCart) {
        subject.orderPromotion = [];
      }

      if (subject && updatedCart) {
        subject.orderPromotion = updatedCart.orderPromotion;
        subject.shipping = updatedCart.shipping;
        subject.shippingAddressRequired = updatedCart.shippingAddressRequired;
        subject.availableShippingMethods = updatedCart.availableShippingMethods;
        subject.orderTotalAmount = updatedCart.totalAmount;
        subject.subjectLastName = updatedCart.subjectLastName;
        // update order lines
        updateOrderLines(subject.packages, updatedCart.packages);
        updateOrderLines(subject.addOns, updatedCart.addOns);
        updateOrderLines(subject.bundles, updatedCart.bundles);
        updateOrderLines(subject.upgrades, updatedCart.upgrades);
        subject.fees = updatedCart.fees;
      }
    });

    if (!!updatedCoupons) {
      // Replace whole coupons with updatedCoupons, coupon will have the couponCode and promotion details, as we have to pass all promotion details to pricing service..
      coupons.splice(0, coupons.length);
      R.forEach(
        ((coupon) => {
          R.forEach((subject) => {
            if (subject.shipping) {
              const shippingPromotions = subject.shipping.orderable ? subject.shipping.orderable.promotions : [];
              if (
                !!R.find(R.propEq('couponCode', coupon.couponCode))(shippingPromotions) &&
                !R.find(R.propEq('couponCode', coupon.couponCode))(subject.orderPromotion)
              ) {
                coupon.isShipping = true; // if shipping coupon, we will wave of the shipping charge and we will not display the coupon.
              }
            }
          })(subjects);
          coupons.push(coupon);
        }).bind(coupons),
        updatedCoupons
      );
    }
    this.loadFees();
    this.updateSubjectsTotalAmount();
    this.totalAmount = updatedOrders?.reduce((acc, order) => acc + order.totalAmount, 0);
    this.updateStorage();
    this.syncFreeGiftState();
    // Signal that a reprice has fully settled (prices, fees, shippingAddressRequired).
    this.repriceVersion++;
  };

  this.resetPriceAdjustment = function (orderIndex) {
    const subjectList = [];
    this.fees = [];
    if (orderIndex >= 0) {
      subjectList.push(this.getSubjectOrder(orderIndex));
    } else {
      R.forEach((subject) => {
        subjectList.push(subject);
      }, subjects);
    }
    const resetOrderLinePriceAdjustment = function (orderLine) {
      orderLine.salesPrice = orderLine.orderable.unitPrice;
      orderLine.orderPromotion = [];
    };

    if (!!coupons && !R.isEmpty(coupons)) {
      coupons.splice(0, coupons.length);
    }

    this.totalAmount = subjectList.reduce((acc, subject) => acc + subject.getSubTotal(), 0);

    subjectList.map((subject) => {
      if (subject) {
        subject.taxAmount = 0;
        subject.orderPromotion = [];
        subject.shipping = [];
        subject.fees = [];

        R.forEach(resetOrderLinePriceAdjustment)(subject.packages);
        R.forEach(resetOrderLinePriceAdjustment)(subject.addOns);
        R.forEach(resetOrderLinePriceAdjustment)(subject.bundles);
        R.forEach(resetOrderLinePriceAdjustment)(subject.upgrades);

        this.updateStorage();
      }
    });
  };

  this.getCurrentSubject = function () {
    if (R.indexOf(activeSubject, subjects) == -1) {
      activeSubject = subjects[0];
    }
    return activeSubject;
  };

  this.getCurrentSubjectIndex = function () {
    return R.indexOf(activeSubject, subjects) == -1 ? 0 : R.indexOf(activeSubject, subjects);
  };

  this.isEnhancedPayonlyJobLoaded = function (pictureDayData) {
    const subject = R.find(
      (subject) =>
        subject.portraitDashboard.portraitId === pictureDayData.portraitId &&
        subject.portraitDashboard.accessCode === pictureDayData.accessCode
    )(this.getSubjects());
    return subject && JobStore.isEnhancedPayOnly(subject.portraitDashboard.jobKey);
  };

  this.getPictureDayData = function (subject) {
    let pictureDayData;
    if (!subject) {
      subject = activeSubject;
    }
    if (subject.portraitDashboard.portraitId) {
      pictureDayData = {
        portraitId: subject.portraitDashboard.portraitId,
        accessCode: subject.portraitDashboard.accessCode,
        firstName: subject.subjectFirstName
      };
    } else {
      pictureDayData = {
        pictureDayId: subject.portraitDashboard.event,
        firstName: subject.subjectFirstName
      };
    }
    return pictureDayData;
  };

  this.addSubject = function (subject) {
    const jobContext = JobStore.getJob();

    subject.id = GuidGenerator.generateGuid();
    subject.createdDateTime = new Date();
    subjects.push(subject);
    this.orderOpen = true;

    // make sure new subject has portraitDashboard attached to it
    if (!subject.portraitDashboard) {
      subject.portraitDashboard = {
        subjectFirstName: subject.subjectFirstName,
        subjectRelationship: subject.subjectRelationship,
        portraitId: jobContext.portraitId,
        accessCode: jobContext.accessCode,
        clickSelection: jobContext.clickSelection,
        event: jobContext.jobNumber,
        sellingMethod: jobContext.sellingMethod,
        spectrumEnabled: jobContext.spectrumEnabled,
        accountName: jobContext.accountName,
        pictureDayExpirationDate: jobContext.pictureDayExpirationDate,
        claimExpirationDate: jobContext.claimExpirationDate,
        lifetouchUserProfileId: UserProfileStore.userProfile.lifetouchUserProfileId,
        email: UserProfileStore.userProfile.email,
        eventDate: jobContext.eventDate,
        lastPictureDay: jobContext.lastPictureDayDate,
        lifetouchId: jobContext.lifetouchId,
        jobKey: jobContext.jobKey,
        jobName: jobContext.jobName
      };
    }
    this.setCurrentSubject(R.indexOf(subject, subjects));
  };

  this.removeSubject = function (id, isJobToBeDeleted) {
    let subjectIndex;
    if (id) {
      subjectIndex = this.getSubjectIndex(id);
    } else {
      subjectIndex = this.getCurrentSubjectIndex();
    }
    const subjectToDelete = subjects[subjectIndex];
    subjects.splice(subjectIndex, 1);

    if (subjectToDelete && isJobToBeDeleted === true) {
      this.removeJobContextWhenOrphan(subjectToDelete);
    }
    if (subjects.length > 0) {
      this.setCurrentSubject(0);
    } else {
      coupons = []; // clear the coupons if there are no orders.
      EmptyCartAction.dispatch();
    }
    this.removeFromOrderIdsHistory(subjectToDelete.id);
    this.updateStorage();
    return this.repriceCart();
  };

  this.removeJobContextWhenOrphan = function (subjectToDelete) {
    if (subjectToDelete.portraitDashboard) {
      const jobKey = subjectToDelete.portraitDashboard.jobKey;

      const index = R.findIndex((subject) => subject.portraitDashboard.jobKey === jobKey)(this.getSubjects());

      if (index === -1) {
        JobStore.removeJobContextByJobKey(jobKey);
      }
    }
  };

  this.getSubjectJobData = function (subject) {
    const jobContext = JobStore.getJobBySubject(subject);
    if (jobContext) {
      return {
        apoKey: jobContext.apoKey,
        currencyName: jobContext.currency,
        pricingTypeName: jobContext.pricingType,
        jobNumber: jobContext.jobNumber,
        accessCredential: JobStore.getAccessCredential(jobContext.jobKey),
        orderType: jobContext.orderType,
        specOrderFlow: jobContext.specOrderFlow,
        clickSelection: JobStore.getClickSelection(jobContext.jobKey),
        captureSessionId: JobStore.getCaptureSessionId(jobContext.jobKey),
        jobName: jobContext.jobName,
        jobCity: jobContext.jobCity,
        jobCountry: jobContext.jobCountry,
        jobState: jobContext.jobState,
        sellingMethod: jobContext.sellingMethod,
        paymentAccount: jobContext.paymentAccount,
        currency: jobContext.currency,
        canadianJob: jobContext.canadianJob,
        eventDate: jobContext.eventDate,
        lastPictureDayDate: jobContext.lastPictureDayDate,
        shipToHome: jobContext.shipOrdersTo === 'Subject' ? true : jobContext.shipToHome,
        accountName: jobContext.accountName,
        photographNonBuyers: jobContext.photographNonBuyers
      };
    }
    return null;
  };

  const stripGeneratedPlaceholderFromSelection = function (selection) {
    if (!selection || !SubjectStore.isGeneratedFirstNamePlaceholder(selection.value)) {
      return;
    }

    selection.value = '';
  };

  const stripGeneratedPlaceholderFromItem = function (item) {
    if (!item) {
      return;
    }

    (item.orderLineItemSelections || []).forEach(stripGeneratedPlaceholderFromSelection);
    (item.upgrades || []).forEach(stripGeneratedPlaceholderFromItem);
  };

  const stripGeneratedPlaceholderFromOrderLine = function (orderLine) {
    if (!orderLine) {
      return;
    }

    stripGeneratedPlaceholderFromItem(orderLine.orderable);
    (orderLine.orderLineDetails || []).forEach((detail) => stripGeneratedPlaceholderFromItem(detail?.item));
  };

  const stripGeneratedPlaceholderFromSubmission = function (subjectData) {
    const shouldStripGeneratedName =
      !!subjectData?.subjectDetailsDeferred &&
      SubjectStore.isGeneratedFirstNamePlaceholder(subjectData.subjectFirstName);

    if (!shouldStripGeneratedName) {
      return subjectData;
    }

    subjectData.subjectFirstName = null;

    if (SubjectStore.isGeneratedFirstNamePlaceholder(subjectData.nameOnText)) {
      subjectData.nameOnText = null;
    }

    if (SubjectStore.isGeneratedFirstNamePlaceholder(subjectData?.portraitDashboard?.subjectFirstName)) {
      subjectData.portraitDashboard.subjectFirstName = null;
    }

    [subjectData.packages, subjectData.addOns, subjectData.bundles, subjectData.upgrades].forEach((collection) =>
      (collection || []).forEach(stripGeneratedPlaceholderFromOrderLine)
    );

    return subjectData;
  };

  this.buildSubjectListForSubmission = function (include) {
    const subjectList = [];
    R.forEach((subject) => {
      if (!!subject.isStandardizedPricing) {
        if (!StandardizedEnhancementService.validateSubjectEnhancements(subject)) {
          StandardizedEnhancementService.syncSubjectEnhancements(subject);
        }
      }
      if (!!subject.getOrderLines() && subject.getOrderLines().length > 0) {
        if (JobStore.getJobBySubject(subject).spectrumEnabled || include) {
          subject.jobContext = subject.jobContext || this.getSubjectJobData(subject);
          const subjectData = JSON.parse(JSON.stringify(subject));
          subjectList.push(stripGeneratedPlaceholderFromSubmission(subjectData));
        }
      }
    }, subjects);
    return subjectList;
  };

  this.buildSubjectListForTaxSubmission = function () {
    const subjectList = [];
    R.forEach((subject) => {
      if (!!subject.getOrderLines() && subject.getOrderLines().length > 0) {
        if (
          !JobStore.isPayOnly(subject.portraitDashboard.jobKey) &&
          JobStore.getJobBySubject(subject).spectrumEnabled
        ) {
          subject.jobContext = this.getSubjectJobData(subject);
          subjectList.push(subject);
        }
      }
    }, subjects);
    return subjectList;
  };

  this.hasFreeGiftCoupon = () =>
    this.getCoupons()?.some((coupon) => this.isAutoApplyCoupon(coupon) && !coupon.shippingCoupon);

  this.syncFreeGiftState = () => {
    const context = this.getFreeGiftContext();
    if (!context) return;

    const { subtotal, giftThreshold, hasFreeGiftCoupon } = context;

    const shouldHaveGift = subtotal >= giftThreshold || hasFreeGiftCoupon;

    if (shouldHaveGift && !this.hasFreeGift()) {
      this.addFreeGiftToCart(context);
    } else if (!shouldHaveGift && this.hasFreeGift()) {
      this.removeFreeGift();
    }
  };

  this.getFreeGiftContext = () => {
    const CartRewardTrackerService = $injector.get('CartRewardTrackerService');
    const freeGiftCoupon = CartRewardTrackerService.getFreeGiftCoupon();
    if (!freeGiftCoupon) return;

    const giftThreshold = Number(freeGiftCoupon.orderMinCost);
    if (!giftThreshold) return;

    const hasFreeGiftCoupon = this.hasFreeGiftCoupon();
    /* We subtract the price of the free gift because once the subtotal falls below the threshold on the backend,
     the gift is no longer free, gets a price assigned to it, and is then included in totalAmount.
     That’s why we remove it here. Ideally, free gift items should be excluded from totalAmount on the backend.
     An even better solution would be to handle free gift creation on the backend as well. */
    const subtotal = this.getSubtotalAfterDiscounts() - (this.getFreeGiftAddOn()?.salesPrice || 0);

    return {
      freeGiftCoupon,
      giftThreshold,
      hasFreeGiftCoupon,
      subtotal
    };
  };

  let repriceSeq = 0;
  let repriceDebounceTimer = null;
  let repriceDeferred = null;

  this.repriceCart = function (orderIndex) {
    const seq = ++repriceSeq;

    if (!FeatureFlagService.isEnabled('REPRICE_CART_DEBOUNCE')) {
      const deferred = $q.defer();
      this._repriceCart(seq, orderIndex, deferred);
      return deferred.promise;
    }

    if (repriceDebounceTimer) {
      $timeout.cancel(repriceDebounceTimer);
    }

    if (!repriceDeferred) {
      repriceDeferred = $q.defer();
    }
    const deferred = repriceDeferred;

    const debounceMs = window.repriceCartDebounceTimeInMs || ShoppingCartConstants.REPRICE_DEBOUNCE_MS;

    repriceDebounceTimer = $timeout(() => {
      repriceDebounceTimer = null;
      repriceDeferred = null;
      this._repriceCart(seq, orderIndex, deferred);
    }, debounceMs);

    return deferred.promise;
  };

  this._repriceCart = function (seq, orderIndex, deferred) {
    let subjectList;
    try {
      subjectList = this.buildSubjectListForSubmission(false);
    } catch (e) {
      loggerService.error('repriceCart sync error', e);
      deferred.reject(e);
      return;
    }

    if (!subjectList.length) {
      this.resetPriceAdjustment(orderIndex);
      AnalyticsService.updateContentView();
      const p = $q.when(UserProfileStore.isLoggedIn() ? PersistentCartService.delete() : true);
      p.then((v) => deferred.resolve(v)).catch((e) => deferred.reject(e));
      return;
    }

    $http
      .post('cart/adjustPricing', {
        shippingAddress: this.shippingAddress,
        orders: subjectList,
        coupons: coupons,
        payment: this.payment,
        context: { applyFamilyDiscountCoupon: this.familyDiscountActivated }
      })
      .then((response) => {
        if (FeatureFlagService.isEnabled('REPRICE_CART_SEQUENCE') && seq !== repriceSeq) {
          loggerService.warn('ShoppingCartStore.repriceCart: stale response discarded', { seq, repriceSeq });
          deferred.resolve();
          return;
        }

        AnalyticsService.updateContentView();
        AnalyticsService.recordEvent(AnalyticsService.events.CART_REPRICE);

        if (!response.data.success) {
          this.resetPriceAdjustment(orderIndex);
          deferred.resolve();
          return;
        }

        return $q
          .when(this.adjustPricing(response.data.orders, orderIndex, response.data.coupons))
          .then((v) => deferred.resolve(v));
      })
      .catch((error) => {
        loggerService.error('repriceCart failed', error);
        this.resetPriceAdjustment();
        deferred.reject(error);
      });
  };

  this.adjustTax = function (orders) {
    R.forEachIndexed((subject, orderIndex) => {
      const order = R.find(R.propEq('id', subject.id))(orders);
      if (order) {
        subject.taxAmount = order.taxAmount;
        subject.totalAmount = order.totalAmount + subject.taxAmount;
      }
    }, subjects);
  };

  let taxSeq = 0;

  this.calculateTax = function () {
    const seq = ++taxSeq;
    const subjectList = this.buildSubjectListForTaxSubmission();
    R.forEach((subject) => {
      subject.totalAmount = subject.orderTotalAmount;
    }, subjectList);

    // call a pricing service to get cart's pricing and update multi orders.
    return $http
      .post('cart/calculateTax', {
        shippingAddress: this.shippingAddress,
        orders: subjectList,
        coupons: coupons,
        payment: this.payment,
        context: { applyFamilyDiscountCoupon: this.familyDiscountActivated }
      })
      .then((response) => {
        // Discard a stale response if a newer calculateTax has since been issued, so
        // out-of-order responses can't overwrite fresher tax (mirrors repriceSeq).
        if (seq !== taxSeq) {
          loggerService.warn('ShoppingCartStore.calculateTax: stale response discarded', { seq, taxSeq });
          return;
        }
        if (response.data.success) {
          // adjust tax
          this.adjustTax(response.data.orders);
          AnalyticsService.updateContentView();
        } else {
          AnalyticsService.updateContentView();
        }
      })
      .catch((error) => {
        loggerService.error('calculateTax failed', error);
        console.error('calculateTax failed', error);
        AnalyticsService.updateContentView();
        return $q.reject(error);
      });
  };

  this.repriceEnhancedPayOnly = function (subjectOrder) {
    subjectOrder.jobContext = this.getSubjectJobData(subjectOrder);
    const dfd = $q.defer();
    const subjectList = [];
    subjectList.push(subjectOrder);
    // call a pricing service to get cart's pricing
    $http
      .post('cart/adjustPricing', {
        orders: subjectList,
        captureSessionId: JobStore.captureSessionId,
        clickSelection: JobStore.clickSelection
      })
      .then((response) => {
        if (response.data.success) {
          this.setSalesPrice(response.data.orders);
          this.updateStorage();
          AnalyticsService.updateContentView();
          dfd.resolve();
        } else {
          AnalyticsService.updateContentView();

          dfd.reject();
        }
      })
      .catch(() => {
        AnalyticsService.updateContentView();
        dfd.reject();
      });

    return dfd.promise;
  };

  this.repriceEnhancedPayOnlyPackage = function (_package, payOnlySubjectOrder) {
    payOnlySubjectOrder.packages = [];
    payOnlySubjectOrder.addPackage(_package);
    return this.repriceEnhancedPayOnly(payOnlySubjectOrder);
  };

  this.setSalesPrice = function (updatedCart) {
    this.payOnlySalesPrice = updatedCart[0].packages[0].salesPrice;
  };

  this.getSalesPrice = function () {
    return this.payOnlySalesPrice;
  };

  this.addOrUpdatePackage = function (
    _package,
    upgrades,
    index,
    orderIndex,
    nameChanged,
    favouriteLooks,
    repriceCart = true
  ) {
    const subject = this.getSubjectOrder(orderIndex);
    if (subject) {
      if (ProductCatalogStore.isStandardizedPricingProgram()) {
        if (nameChanged) {
          StandardizedEnhancementService.syncSubjectEnhancements(this.getCurrentSubject());
        }
      }
      validateItem('package', _package);
      // MLC-1141 MarkN 2016.10.21 if in the enhanced pay only flow then make sure
      // we don't add more than one package
      if (JobStore.isEnhancedPayOnly() && subject.packages && subject.packages.length > 0) {
        index = 0;
      }
      if (index >= 0) {
        subject.updatePackage(_package, index);
      } else {
        subject.addPackage(_package);
      }
      if (!JobStore.isEnhancedPayOnly()) {
        this.updateUpgrades(upgrades, null, orderIndex);
        this.refreshSubject(orderIndex);
      }
      subject.favouriteLooks = favouriteLooks;
      return repriceCart ? this.repriceCart() : $q.resolve();
    }
  };

  this.addAddOn = function (addOn, repriceCart = true) {
    const subject = this.getCurrentSubject();

    if (subject) {
      validateItem('add-on', addOn);
      subject.addAddOn(addOn);
      if (JobStore.getJobBySubject(subject).spectrumEnabled) {
        this.refreshSubject();
        return repriceCart ? this.repriceCart() : $q.resolve();
      }
    }
  };

  this.addAddOnToSubjectByJobNumber = (jobNumber, addOn) => {
    const subject = this.getSubjectByJobNumber(jobNumber);

    return this.addAddOnToSubjectBySubject(subject, addOn);
  };

  this.addAddOnToSubjectBySubject = (subject, addOn) =>
    $q.resolve().then(() => {
      validateItem('add-on', addOn);
      const orderLine = OrderLineFactory.createFromAddOn(addOn, null);

      subject?.addAddOn(orderLine);
      return subject;
    });

  this.addAddOnToSubjectBySubjectAndReprice = (subject, addOn) =>
    this.addAddOnToSubjectBySubject(subject, addOn).then(() => this.repriceCart());

  this.removeFreeGift = function () {
    const subjects = this.getSubjects();

    const subject = subjects?.find((s) => s.addOns?.some((addOn) => addOn.orderable?.freeGift === true));

    if (!subject) return;

    const addOnIndex = subject.addOns.findIndex((addOn) => addOn.orderable?.freeGift === true);

    if (addOnIndex === -1) return;

    this.removeAddOn(addOnIndex, this.getSubjectIndex(subject.id));
    $rootScope.$emit('shoppingCart:freeGiftRemoved');
  };

  this.addAddOnToSubjectById = function (subjectMapBySkuSet, repriceCart = true) {
    return $q.resolve().then(() => {
      const promises = subjectMapBySkuSet
        .filter((product) => this.getSubjectByJobNumber(product.jobNumber))
        .map((product) => this.addAddOnToSubjectByJobNumber(product.jobNumber, product.addOn));

      return $q.all(promises).then(() => {
        this.refreshSubject();
        return repriceCart ? this.repriceCart() : $q.resolve();
      });
    });
  };
  this.hasFreeGift = () => Boolean(this.getFreeGiftAddOn());

  this.freeGiftAmount = function () {
    return this.getSubjects()
      ?.map((subject) => {
        const freeGift = subject.addOns?.find((addOn) => addOn.orderable?.freeGift === true);

        if (!freeGift) return 0;

        const hasPackage = subject.packages && subject.packages.length > 0;
        return hasPackage ? freeGift.orderable.discountedPriceWithPackage : freeGift.orderable.salePrice;
      })
      .reduce((total, price) => total + (price || 0), 0);
  };
  this.addOrUpdateAddOns = function (addOns, upgrades, index, orderIndex, repriceCart = true) {
    const subject = this.getSubjectOrder(orderIndex);

    if (!subject) {
      return;
    }

    validateItem('add-ons', addOns);
    // index < 0 -> add all add-ons as new items
    // index >= 0 -> update existing add-on at index with first item, add remaining as new items
    addOns.forEach((addOn, idx) => {
      if (index >= 0 && idx === 0) {
        subject.updateAddOn(addOn, index);
      } else {
        subject.addAddOn(addOn);
      }
    });

    if (JobStore.getJobBySubject(subject).spectrumEnabled) {
      if (upgrades) {
        this.updateUpgrades(upgrades, null, orderIndex);
      }

      this.refreshSubject(orderIndex);

      return repriceCart ? this.repriceCart() : $q.resolve();
    }
  };
  this.addOrUpdateBundle = function (bundle, upgrades, index, orderIndex, repriceCart = true) {
    const subject = this.getSubjectOrder(orderIndex);
    let upgradesRemoved = false;
    const hasPersonalizationEnhancement = R.any((e) => R.contains(e.type)([UpgradeType.PERSONALIZATION]));
    const hasRetouchingEnhancement = R.any((e) =>
      R.contains(e.type)([UpgradeType.BASIC_RETOUCHING, UpgradeType.PREMIUM_RETOUCHING])
    );

    if (subject) {
      validateItem('bundle', bundle);
      this.validateBundle(index, orderIndex, bundle);

      if (bundle.orderable.upgrades && bundle.orderable.upgrades.length) {
        if (
          hasPersonalizationEnhancement(R.pluck('orderable')(subject.upgrades)) &&
          hasPersonalizationEnhancement(bundle.orderable.upgrades)
        ) {
          this.removeUpgrade(false, false, repriceCart, orderIndex);
          upgradesRemoved = true;
        }

        if (
          hasRetouchingEnhancement(R.pluck('orderable')(subject.upgrades)) &&
          hasRetouchingEnhancement(bundle.orderable.upgrades)
        ) {
          this.removeUpgrade(true, false, repriceCart, orderIndex);
          upgradesRemoved = true;
        }
      }

      if (index >= 0) {
        subject.updateBundle(bundle, index);
      } else {
        subject.addBundle(bundle);
      }

      // upgrades can be passed along from bundle detail page
      if (!!upgrades) {
        this.updateUpgrades(upgrades, null, orderIndex);
      }

      this.refreshSubject(orderIndex);

      return repriceCart
        ? this.repriceCart().then(
            () => ({ upgradesRemoved }),
            () => ({ upgradesRemoved })
          )
        : $q.resolve();
    }
  };

  this.validateBundle = function (index, orderIndex, bundle) {
    const subject = this.getSubjectOrder(orderIndex);
    const hasPersonalizationEnhancement = R.any((e) => R.contains(e.type)([UpgradeType.PERSONALIZATION]));
    const hasRetouchingEnhancement = R.any((e) =>
      R.contains(e.type)([UpgradeType.BASIC_RETOUCHING, UpgradeType.PREMIUM_RETOUCHING])
    );

    if (subject.bundles.length && (index === undefined || index < 0) && bundle && bundle.orderable) {
      const currentBundleHasRetouch = hasRetouchingEnhancement(bundle.orderable.upgrades);
      const currentBundleHasNogo = hasPersonalizationEnhancement(bundle.orderable.upgrades);
      const orderableOfAllBundles = R.pluck('orderable')(subject.bundles);
      let alreadyHasNogoInBundle = false;
      let alreadyHasRetouchInBundle = false;

      orderableOfAllBundles.forEach((entry) => {
        if (hasPersonalizationEnhancement(entry.upgrades)) {
          alreadyHasNogoInBundle = true;
        }
        if (hasRetouchingEnhancement(entry.upgrades)) {
          alreadyHasRetouchInBundle = true;
        }
      });

      if (alreadyHasRetouchInBundle && currentBundleHasRetouch) {
        throw new ValidationException('ONLY_ONE_RETOUCH_BUNDLE_ALLOWED_ERROR');
      }

      if (alreadyHasNogoInBundle && currentBundleHasNogo) {
        throw new ValidationException('ONLY_ONE_NOGO_BUNDLE_ALLOWED_ERROR');
      }
    }
  };

  this.removePackage = function (index, orderIndex) {
    const subject = this.getSubjectOrder(orderIndex);
    const warningMessages = [];
    if (subject) {
      AnalyticsService.recordRemoveFromCart(subject.packages[index].orderable);
      subject.deletePackage(index);
      subject.addOns = subject.addOns.filter((on) => on.orderable.type !== 'BACKGROUND');
      warningMessages.push(this.resetUpgradesIfNeeded(orderIndex));

      this.refreshSubject(orderIndex);

      return this.repriceCart().then(
        () => ({ warningMessages: R.flatten(warningMessages) }),
        () => ({ warningMessages: R.flatten(warningMessages) })
      );
    }
  };

  this.removeBundle = function (index, orderIndex) {
    const subject = this.getSubjectOrder(orderIndex);
    if (subject) {
      AnalyticsService.recordRemoveFromCart(subject.bundles[index].orderable);
      subject.deleteBundle(index);
      this.refreshSubject(orderIndex);

      return this.repriceCart();
    }
  };

  this.removeAddOn = function (index, orderIndex) {
    const subject = this.getSubjectOrder(orderIndex);

    if (subject) {
      AnalyticsService.recordRemoveFromCart(subject.addOns[index].orderable);
      subject.deleteAddOn(index);
      if (!JobStore.isPayOnly()) {
        const warningMessages = this.resetUpgradesIfNeeded(orderIndex);
        this.refreshSubject(orderIndex);

        return this.repriceCart().then(
          () => ({ warningMessages }),
          () => ({ warningMessages })
        );
      }
    }
  };

  this.resetUpgradesIfNeeded = function (orderIndex) {
    const warningMessages = [];
    const subject = this.getSubjectOrder(orderIndex);

    if (!subject.packages.length && !subject.addOns.length && subject.upgrades.length) {
      // there are no packages nor addons.. delete and reset upgrades
      R.forEachIndexed((idx) => {
        subject.deleteUpgrade(idx);
      })(subject.upgrades);
      subject.optOutOfPersonalizationEnhancements = false;
      subject.optOutOfRetouchingEnhancements = false;

      this.updateStorage();

      warningMessages.push('UPGRADE_DELETED_NO_ITEMS');
    }

    return warningMessages;
  };

  this.getFreeGiftDiscountTotal = (subject) => {
    if (!this.hasFreeGift()) return 0.0;

    const freeGift = subject.addOns.find((orderLine) => orderLine.orderable.freeGift === true);
    return freeGift ? freeGift.orderable.unitPrice : 0.0;
  };

  this.getOrderSubTotal = function (orderIndex) {
    const subject = this.getSubjectOrder(orderIndex);
    if (!subject) return 0.0;
    const freeGiftDiscountTotal = this.getFreeGiftDiscountTotal(subject);
    return subject.getSubTotal() - freeGiftDiscountTotal;
  };

  this.getOrderTotal = function (orderIndex) {
    const subject = this.getSubjectOrder(orderIndex);
    return !!subject ? subject.getTotal() : 0.0;
  };

  this.getSlashThroughPrices = function (orderIndex) {
    const subject = this.getSubjectOrder(orderIndex);
    return !!subject ? subject.getSlashThroughPrices() : 0.0;
  };

  this.getOrderShippingPrice = function (orderIndex) {
    const subject = this.getSubjectOrder(orderIndex);
    return !!subject ? subject.getShippingPrice() : 0.0;
  };

  this.getDisplayName = function () {
    return !!this.getCurrentSubject() ? this.getCurrentSubject().getDisplayName() : '';
  };

  this.getPromotionAmount = function () {
    return R.sum(R.map((subject) => subject.getPromotionAmount())(subjects));
  };

  this.getNumberOfItemsInCart = function () {
    return R.sum(R.map((subject) => subject.getNumberOfItemsInCart())(subjects));
  };

  this.getNumberOfItemsInCartAndEnhancements = function () {
    return R.sum(R.map((subject) => subject.getNumberOfItemsInCart() + subject.upgrades.length)(subjects));
  };

  this.getNumberOfItemsInOrderById = (id) => this.getNumberOfItemsBySubject(this.getSubjectById(id));

  this.getNumberOfItemsInOrder = (orderIndex) => this.getNumberOfItemsBySubject(this.getSubjectOrder(orderIndex));

  this.getNumberOfItemsBySubject = (subjectOrder) => {
    if (!subjectOrder) {
      return 0;
    }
    return subjectOrder.getNumberOfItemsInCart() + subjectOrder.upgrades.length;
  };

  this.getCurrentSubjectItemsCount = function () {
    return !!this.getCurrentSubject() ? this.getCurrentSubject().getNumberOfItemsInCart() : 0;
  };

  this.getOrderPromotionAmount = function (orderIndex) {
    return !!this.getSubjectOrder(orderIndex) ? this.getSubjectOrder(orderIndex).getPromotionAmount() : '-';
  };

  let callCount = 0;
  this.clearAll = function (isSessionTimeout) {
    activeSubject = null;
    orderIdsHistory = [];
    subjects = [];
    coupons = [];
    this.fees = [];
    this.payment = {};
    this.shippingAddress = {};
    this.suggestedAddress = {};
    this.orderOpen = false;
    this.totalTaxAmount = 0;
    this.totalAmount = 0;
    this.clientAuthorization = {};
    this.familyDiscountActivated = false;
    this.updateStorage();
    EnhancementStore.clearEnhancementStore();
    if (!isSessionTimeout) {
      $http.put('cart/clearSessionData', {});
    }
    $timeout(() => JobStore.clearAll());

    try {
      callCount++;
      if (callCount === 100) {
        callCount = 0;
        const error = new Error();
        error.message = `ShoppingCartStore.clearAll called too much! ${JSON.stringify({
          route: StorageService.get('data.router')
        })}`;
        ErrorLogService(error);
      }
    } catch (e) {}
  };

  // add/remove upgrades
  this.updateUpgrades = function (upgrades, repriceCart, orderIndex) {
    upgrades.forEach((upgrade) => {
      if (!!upgrade.orderLine) {
        if (upgrade.isRetouching) {
          this.addRetouching(upgrade.orderLine, orderIndex);
        } else {
          this.addPersonalization(upgrade.orderLine, orderIndex);
        }
      } else {
        this.removeUpgrade(upgrade.isRetouching, upgrade.optOut, null, orderIndex);
      }
    });
    this.refreshSubject(orderIndex);
    if (repriceCart) {
      return this.repriceCart();
    }

    return $q.when(true);
  };

  this.addPersonalization = function (orderLine, orderIndex) {
    const subject = this.getSubjectOrder(orderIndex);
    if (subject) {
      this.removeUpgrade(false, false, false, orderIndex);
      subject.addUpgrade(orderLine);
      subject.optOutOfPersonalizationEnhancements = false;
    }
  };

  this.addRetouching = function (orderLine, orderIndex) {
    const subject = this.getSubjectOrder(orderIndex);
    if (subject) {
      this.removeUpgrade(true, false, false, orderIndex);
      subject.addUpgrade(orderLine);
      subject.optOutOfRetouchingEnhancements = false;
    }
  };

  this.removeUpgrade = function (isRetouching, optOut, reprice, orderIndex) {
    const subject = this.getSubjectOrder(orderIndex);
    const sizeBeforeUpdate = R.length(subject.upgrades);

    if (subject) {
      subject.upgrades = R.filter(
        (e) => isRetouching === R.propEq('type', UpgradeType.PERSONALIZATION)(e.orderable),
        subject.upgrades
      );

      if (isRetouching) {
        subject.optOutOfRetouchingEnhancements = optOut;
      } else {
        subject.optOutOfPersonalizationEnhancements = optOut;
      }

      let promise;
      // reprice only if cart is updated and caller need to reprice cart
      if (sizeBeforeUpdate !== R.length(subject.upgrades) && !!reprice) {
        promise = this.repriceCart();
      } else {
        this.updateStorage();
        promise = $q.when(true);
      }

      return promise;
    }
  };

  this.getNoGoEnhancementData = function (orderIndex) {
    const selectedUpgrades = this.getSubjectOrder(orderIndex).getUpgradesByType();
    const name = selectedUpgrades.personalizationEnhancement
      ? selectedUpgrades.personalizationEnhancement.orderLineDetails[0].item.orderLineItemSelections[0].value
      : '';
    return {
      name
    };
  };

  /**
   * Gets the current subject personalization value for UI prefill.
   * Reuses the explicit personalization enhancement value when present,
   * otherwise falls back to the explicit nameOnText value stored on the subject.
   * If neither exists, returns an empty string.
   * @returns {string}
   */
  this.getCurrentNoGoEnhancementData = function () {
    const currentSubject = this.getCurrentSubject();
    const selectedUpgrades = currentSubject.getUpgradesByType();

    let name = selectedUpgrades.personalizationEnhancement?.orderLineDetails[0].item.orderLineItemSelections[0].value;

    if (!name) {
      name = this.getCurrentSubjectNameOnText();
    }

    if (
      !name &&
      currentSubject.allowPersonalizationNamePrefill &&
      currentSubject.subjectFirstName &&
      !SubjectStore.isGeneratedFirstNamePlaceholder(currentSubject.subjectFirstName)
    ) {
      name = currentSubject.subjectFirstName.trim();
    }

    if (!name) {
      name = '';
    }

    return name;
  };

  this.getSelectedEnhancements = function (orderIndex) {
    return this.getSubjectOrder(orderIndex).upgrades;
  };

  this.isPersonalizationEnhancementOptedOut = function (orderIndex) {
    return this.getSubjectOrder(orderIndex).optOutOfPersonalizationEnhancements;
  };

  this.isRetouchingEnhancementOptedOut = function (orderIndex) {
    return this.getSubjectOrder(orderIndex).optOutOfRetouchingEnhancements;
  };

  this.isUpgradeDataValid = function (orderIndex) {
    // compares whats in subject Vs whats available...
    const subject = this.getSubjectOrder(orderIndex);
    const availableEnhancements = this.getAvailableEnhancements(null, orderIndex);
    const availablePersonalizationEnhancements = R.filter(R.propEq('type', UpgradeType.PERSONALIZATION))(
      availableEnhancements
    );
    const availableRetouchingEnhancements = R.filter(R.compose(R.not, R.propEq('type', UpgradeType.PERSONALIZATION)))(
      availableEnhancements
    );

    const isValidUpgrade = function (availableUpgrades, value, optOut) {
      return !availableUpgrades.length || (!!availableUpgrades.length && (!!value || !!optOut));
    };

    const selectedUpgrades = subject.getUpgradesByType();
    return (
      isValidUpgrade(
        availableRetouchingEnhancements,
        selectedUpgrades.retouchingEnhancement,
        subject.optOutOfRetouchingEnhancements
      ) &&
      isValidUpgrade(
        availablePersonalizationEnhancements,
        selectedUpgrades.personalizationEnhancement,
        subject.optOutOfPersonalizationEnhancements
      ) &&
      isValidUpgrade(
        availablePersonalizationEnhancements,
        this.getNoGoEnhancementData(orderIndex).name,
        subject.optOutOfPersonalizationEnhancements
      )
    );
  };

  //Return the orderable items in the cart
  this.getOrderLines = function () {
    return R.map((subject) => ({
      name: subject.subjectFirstName,
      lastName: subject.subjectLastName,
      order: subject.getOrderLines(),
      schoolName: subject.portraitDashboard.accountName,
      jobKey: subject.portraitDashboard.jobKey,
      payOnly: JobStore.isPayOnly(subject.portraitDashboard.jobKey),
      collapsed: false,
      addOnsCollapsed: false,
      specialOffersCollapsed: false
    }))(subjects);
  };

  this.getAvailableEnhancements = function (currentItems, subjectIndex) {
    const subject = this.getSubjectOrder(subjectIndex);

    const itemsInCart = R.map((orderLine) =>
      R.append(orderLine.orderable)(R.filter((item) => !!item)(R.pluck('item')(orderLine.orderLineDetails)))
    )(R.flatten([subject.packages, subject.addOns, subject.bundles]));

    const allItems = R.flatten(
      R.concat(
        R.map((orderable) => R.flatten([orderable, orderable.items || [], orderable.upgrades || []]))(
          currentItems || []
        ),
        itemsInCart
      )
    ).filter(Boolean);

    const pcatalog = JobStore.getProductCatalog(subject.portraitDashboard.jobKey);
    let upgrades = [];

    if (!!pcatalog) {
      upgrades = pcatalog.upgrades;
    }

    const isPersonalizationEnhancement = function (e) {
      return R.contains(e.type)([UpgradeType.PERSONALIZATION]);
    };

    const isRetouchingEnhancement = function (e) {
      return R.contains(e.type)([UpgradeType.BASIC_RETOUCHING, UpgradeType.PREMIUM_RETOUCHING]);
    };

    const containsItemWithLook = R.any(R.compose(R.gt(R.__, 0), R.prop('lookCount')), allItems);
    const containsPersonalizationEnhancement = R.any(isPersonalizationEnhancement)(allItems);
    const containsRetouchingEnhancement = R.any(isRetouchingEnhancement)(allItems);

    const enhancements = R.filter(
      (e) =>
        (isPersonalizationEnhancement(e) && !containsPersonalizationEnhancement) ||
        (isRetouchingEnhancement(e) && containsItemWithLook && !containsRetouchingEnhancement),
      upgrades
    );

    return R.reverse(R.sortBy(R.prop('unitPrice'), enhancements));
  };

  this.toSentenceCase = function (string) {
    if (string) {
      return string.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    }
    return string;
  };

  this.onJobLoaded = function (type, args) {
    FlightRecorderService.log('ShoppingCartStore:onJobLoaded');
    // make sure shopping cart is reset
    const subject = OrderLineFactory.createOrder({
      subjectFirstName: args.firstName,
      subjectRelationship: args.subjectRelationship
    });
    subject.allowPersonalizationNamePrefill = Boolean(
      subject.subjectFirstName && !SubjectStore.isGeneratedFirstNamePlaceholder(subject.subjectFirstName)
    );
    subject.subjectDetailsDeferred = SubjectStore.isGeneratedFirstNamePlaceholder(subject.subjectFirstName);
    // add the first subject into shopping cart
    subject.recentLooks = [];
    this.addSubject(subject);
    if (JobStore.isReorder() || JobStore.isProofOrder()) {
      this.updateSubjectNameFromJobData();
    }

    const jobContext = JobStore.getJob();
    this.setAvailableEnhancements(subject, jobContext.jobKey);
    this.loadPersonalizationTemplate(subject, jobContext.jobKey);
  };

  this.getIncludedUpgradesInSubject = (pendingUpgrades = []) => {
    const subjectPackages = this.getCurrentSubject().packages.map((pkg) => pkg.orderable);
    const upgradesFromPackages = subjectPackages.flatMap((pkg) => ProductCatalogStore.getUpgradesByPackage(pkg));
    const subjectUpgrades = this.getCurrentSubject().upgrades.map((up) => up.orderable);

    const uniqueUpgrades = [...upgradesFromPackages, ...subjectUpgrades, ...pendingUpgrades].reduce((acc, up) => {
      acc.set(up.skuCode, up);
      return acc;
    }, new Map());

    return [...uniqueUpgrades.values()];
  };

  this.setAvailableEnhancements = (subject, jobKey) => {
    EnhancementStore.addAvailableUpgradesByJob({
      id: subject.id,
      upgrades: JobStore.getProductCatalog(jobKey)?.upgrades || []
    });
  };

  this.loadPersonalizationTemplate = (subject, jobKey) => {
    const upgrades = JobStore.getProductCatalog(jobKey)?.upgrades || [];
    upgrades.forEach((upgrade) => {
      if (upgrade.type === UpgradeType.PERSONALIZATION && upgrade.templateName) {
        EnhancementStore.loadTemplateAttempt(upgrade.templateName);
      }
    });
  };

  this.hasOrderItems = (subject) => !!(subject.packages?.length || subject.addOns?.length || subject.bundles?.length);

  this.subjectHasPackagePersonalization = (subject) =>
    (subject.packages || []).some((pkg) =>
      (pkg.orderable?.upgrades || []).some(
        (u) => u.type === UpgradeType.PERSONALIZATION && u.templateName === UpgradeType.NOGO_TEMPLATE_NAME
      )
    ) ||
    (subject.upgrades || []).some(
      (u) =>
        u.orderable?.type === UpgradeType.PERSONALIZATION &&
        u.orderable?.templateName === UpgradeType.NOGO_TEMPLATE_NAME
    );

  this.getPreorderSubjects = () => {
    const subjects = this.getSubjects();

    return subjects.filter((subject) => {
      const needsDetails = subject.subjectDetailsDeferred || !hasRequiredSubjectDetails(subject);

      return needsDetails && this.hasOrderItems(subject) && !this.savedStudentDetailOrderIds.has(subject.id);
    });
  };

  this.updateSavedStudentDetailOrderIds = (id) => this.savedStudentDetailOrderIds.add(id);

  this.updateSubjectNameBeforeCheckout = (subjectsData) => {
    subjects.forEach((subject) => {
      const targetSubject = subjectsData.find((sub) => sub.id === subject.id);
      if (!targetSubject) return;

      subject.subjectFirstName = targetSubject.subjectFirstName;
      subject.subjectLastName = targetSubject.subjectLastName;
      subject.subjectGrade = targetSubject.subjectGrade;
      subject.subjectRelationship = targetSubject.subjectRelationship;
      subject.allowPersonalizationNamePrefill = Boolean(
        subject.subjectFirstName && !SubjectStore.isGeneratedFirstNamePlaceholder(subject.subjectFirstName)
      );
      if (targetSubject.nameOnText !== undefined) {
        const explicitPersonalizationName = targetSubject.nameOnText?.trim() || '';
        const fallbackFirstName = targetSubject.subjectFirstName ? targetSubject.subjectFirstName.trim() : null;
        const personalizationName = explicitPersonalizationName || fallbackFirstName || null;
        subject.nameOnText = personalizationName;
        if (personalizationName) {
          StandardizedEnhancementService.syncSubjectEnhancements(subject);
        }
        if (EnhancementStore.getUpgrades()(subject.id)) {
          EnhancementStore.setPersonalizationName({
            subjectId: subject.id,
            value: {
              value: personalizationName || ''
            }
          });
        }
      }
      subject.subjectDetailsDeferred = false;
      this.updateSavedStudentDetailOrderIds(targetSubject.id);
    });

    this.updateStorage();
  };

  this.applyOptionalPersonalizationFallbackBeforeCheckout = () => {
    let hasChanges = false;

    subjects.forEach((subject) => {
      const hasExplicitPersonalization = Boolean(EnhancementStore.getSelectedPersonalization()(subject.id));

      if (!hasExplicitPersonalization && !subject.hasPackagePersonalization) {
        return;
      }

      const currentName = hasExplicitPersonalization
        ? EnhancementStore.getPersonalizationName()(subject.id)?.trim() || ''
        : subject.nameOnText?.trim() || '';

      if (currentName) {
        return;
      }

      const fallbackFirstName = subject.subjectFirstName ? subject.subjectFirstName.trim() : null;

      if (!fallbackFirstName) {
        return;
      }

      subject.nameOnText = fallbackFirstName;

      if (hasExplicitPersonalization) {
        EnhancementStore.setPersonalizationName({
          subjectId: subject.id,
          value: {
            value: fallbackFirstName
          }
        });
      }

      StandardizedEnhancementService.syncSubjectEnhancements(subject);
      hasChanges = true;
    });

    if (hasChanges) {
      this.updateStorage();
    }
  };

  this.updateSubjectNameFromJobData = function () {
    let jobData;
    if (StorageService.getData('jobData') && StorageService.getData('jobData').length > 0) {
      jobData = StorageService.getData('jobData')[StorageService.getData('jobData').length - 1];
    }
    const subjectDetails = jobData ? jobData.subjectDetails : null;
    if (subjectDetails) {
      const subject = this.getCurrentSubject();
      const firstName = subject.subjectFirstName ? subject.subjectFirstName : subjectDetails.firstName;
      const lastName = subject.subjectLastName ? subject.subjectLastName : subjectDetails.lastName;
      const grade = subject.subjectGrade || subjectDetails.grade || 'GNA';
      subject.subjectFirstName = this.toSentenceCase(firstName);
      subject.subjectLastName = this.toSentenceCase(lastName);
      subject.subjectGrade = grade;
      subject.subjectTeacherName = subjectDetails.teacherName;
      subject.dontKnowTeacherName = subjectDetails.dontKnowTeacherName;
      subject.allowPersonalizationNamePrefill = Boolean(
        subject.subjectFirstName && !SubjectStore.isGeneratedFirstNamePlaceholder(subject.subjectFirstName)
      );
      this.updateStorage();
    }
  };

  this.setAddresses = function (billingAddress, shippingAddress) {
    this.payment.billingAddress = billingAddress;
    this.shippingAddress = shippingAddress;
    this.updateStorage();
  };

  this.loadCreditCardInfo = function (callbackToken) {
    const deferred = $q.defer();
    $http
      .post('checkout/loadCvrCallback', { callbackToken })
      .then((response) => {
        if (!response.data.success) {
          return deferred.reject(response.data.error);
        }

        this.payment.cardNumberDisplay = response.data.data.cardNumberDisplay;
        this.payment.cardExpireMonth = response.data.data.expDate.substring(0, 2);
        this.payment.cardExpireYear = response.data.data.expDate.substring(2, 4);
        this.payment.cardType = response.data.data.cardType;
        this.updateStorage();

        deferred.resolve();
      })
      .catch((e) => {
        deferred.reject(e);
      });

    return deferred.promise;
  };

  this.setCreditCardInfo = function (creditCardDetails) {
    this.payment = {
      paymentType: ContentStore.getEnum('PaymentType').code.CREDIT_CARD,
      paymentMethodNonce: creditCardDetails.nonce,
      cardHolderName: creditCardDetails.details.cardholderName,
      cardNumberDisplay: creditCardDetails.details.lastFour,
      cardExpireMonth: creditCardDetails.details.expirationMonth,
      cardExpireYear: creditCardDetails.details.expirationYear,
      cardType: creditCardDetails.details.cardType,
      clientTokenHash: this.payment.clientTokenHash,
      billingAddress: this.payment.billingAddress,
      retryOrderPK: this.payment.retryOrderPK,
      msgSubId: this.payment.msgSubId
    };
    this.updateStorage();
  };

  this.hasCreditCardInfo = function () {
    return (
      !!this.payment.cardNumberDisplay &&
      !!this.payment.cardExpireMonth &&
      !!this.payment.cardExpireYear &&
      !!this.payment.cardType
    );
  };

  this.isPaymentUsingPayPal = function () {
    return (
      !!this.payment.payPal && this.payment.paymentType?.name === ContentStore.getEnum('PaymentType').code.PAYPAL.name
    );
  };

  this.isPaymentUsingApplePay = function () {
    return (
      !!this.payment.applePay &&
      this.payment.paymentType?.name === ContentStore.getEnum('PaymentType').code.APPLE_PAY.name
    );
  };

  this.isPaymentUsingGooglePay = function () {
    return (
      !!this.payment.googlePay &&
      this.payment.paymentType?.name === ContentStore.getEnum('PaymentType').code.GOOGLE_PAY.name
    );
  };

  this.isPaymentUsingPayPalPayLater = function () {
    return !!this.payment.payPal && !!this.payment.paymentType && this.payment.paymentType.name === 'PAYPALPAYIN4';
  };

  this.isPaymentUsingVenmo = function () {
    return this.payment.paymentType?.name === ContentStore.getEnum('PaymentType').code.VENMO.name;
  };

  this.isPaymentUsingCreditCard = function () {
    return (
      this.hasCreditCardInfo() &&
      this.payment.paymentType?.name === ContentStore.getEnum('PaymentType').code.CREDIT_CARD.name
    );
  };

  this.resetPaypal = function () {
    if (!!this.payment.payPal) {
      this.payment.payPal = null;
      this.updateStorage();
    }
  };

  this.resetApplePay = function () {
    if (!!this.payment.applePay) {
      this.payment.applePay = null;
      this.payment.cardNumberDisplay = null;
      this.updateStorage();
    }
  };

  this.resetGooglePay = function () {
    if (!!this.payment.googlePay) {
      this.payment.googlePay = null;
      this.payment.cardNumberDisplay = null;
      this.updateStorage();
    }
  };

  this.resetVenmo = function () {
    if (!!this.payment.venmo) {
      this.payment.venmo = null;
      this.updateStorage();
    }
  };

  this.resetCreditCardInfo = function () {
    if (this.hasCreditCardInfo()) {
      this.payment.cardNumberDisplay = undefined;
      this.payment.cardExpireMonth = undefined;
      this.payment.cardExpireYear = undefined;
      this.payment.cardType = undefined;
      this.shippingAddress = this.shippingAddress ? this.shippingAddress : {};
      this.updateStorage();
    }
  };

  this.updateStorage = function () {
    StorageService.setData('shoppingCart', {
      subjects,
      activeSubject: subjects.indexOf(activeSubject),
      orderIdsHistory: orderIdsHistory,
      payment: this.payment,
      shippingAddress: this.shippingAddress,
      suggestedAddress: this.suggestedAddress,
      orderOpen: this.orderOpen,
      coupons,
      fees: this.fees,
      clientAuthorization: this.clientAuthorization,
      familyDiscountActivated: this.familyDiscountActivated
    });
    StorageService.setData('studentDetailOrderIds', [...this.savedStudentDetailOrderIds]);
  };

  this.onDeserialized = function (type, data) {
    if (!!data && !!data.shoppingCart) {
      subjects = R.map((subject) => {
        const tempSubject = wrapOrder(subject, subject.portraitDashboard.jobKey);
        tempSubject.subjectAccountConfirmed = subject.subjectAccountConfirmed;
        tempSubject.recentLooks = subject.recentLooks;
        return tempSubject;
      })(data.shoppingCart.subjects);
      orderIdsHistory = data.shoppingCart.orderIdsHistory || [];
      activeSubject = subjects[data.shoppingCart.activeSubject] || null;
      coupons = data.shoppingCart.coupons;
      this.payment = data.shoppingCart.payment;
      this.shippingAddress = data.shoppingCart.shippingAddress;
      this.suggestedAddress = data.shoppingCart.suggestedAddress;
      this.orderOpen = data.shoppingCart.orderOpen;
      this.fees = data.shoppingCart.fees;
      this.clientAuthorization = data.shoppingCart.clientAuthorization;
      this.familyDiscountActivated = data.shoppingCart.familyDiscountActivated;
      this.savedStudentDetailOrderIds = new Set(StorageService.getData('studentDetailOrderIds'));

      subjects.forEach((_, orderIndex) => this._doRefreshSubject(orderIndex));
    }
  };

  this.getStorage = function () {
    return StorageService.getData('shoppingCart');
  };

  this.getPayment = function () {
    return this.payment;
  };

  this.getShippingAddress = function () {
    return this.shippingAddress;
  };

  this.getBillingAddress = function () {
    return this.payment.billingAddress;
  };

  this.getSuggestedAddress = function () {
    return this.suggestedAddress;
  };

  this.setPaymentType = function (paymentType) {
    this.payment.paymentType = paymentType;
    this.updateStorage();
  };

  this.setClientTokenHash = function (clientTokenHash) {
    this.payment.clientTokenHash = clientTokenHash;
    this.updateStorage();
  };

  this.isPaymentMethodApproved = function () {
    return this.payment.paymentMethodNonce && this.payment.paymentMethodNonce.length > 0;
  };

  this.setShippingAddress = function (shippingAddress) {
    const name = extractName(shippingAddress.name);

    this.shippingAddress.email = shippingAddress.email;
    this.shippingAddress.firstName = shippingAddress.firstName || name.firstName;
    this.shippingAddress.lastName = shippingAddress.lastName || name.lastName;
    this.shippingAddress.streetAddress = shippingAddress.streetAddress || shippingAddress.street1;
    this.shippingAddress.aptSuite = shippingAddress.aptSuite || shippingAddress.street2 || '';
    this.shippingAddress.city = shippingAddress.city;
    this.shippingAddress.postalCode = shippingAddress.postalCode;
    this.shippingAddress.phoneNumber = shippingAddress.phoneNumber || shippingAddress.contactPhone;
    this.shippingAddress.stateProvince = shippingAddress.stateProvince;
    this.shippingAddress.isDifferentBillingAddress = shippingAddress.isDifferentBillingAddress;

    if (!!shippingAddress.country) {
      this.shippingAddress.country = shippingAddress.country;
    }

    if (!!shippingAddress.stateProvince) {
      if (!!shippingAddress.stateProvince.name) {
        this.shippingAddress.stateProvince = shippingAddress.stateProvince;
      } else {
        this.shippingAddress.stateProvince = { name: shippingAddress.stateProvince };
      }
    }

    this.updateStorage();
  };

  this.setBillingAddress = function (billingAddress) {
    this.payment.billingAddress = billingAddress;

    this.updateStorage();
  };

  this.setBillingFromShipping = function () {
    if (!this.payment.billingAddress) {
      this.payment.billingAddress = {};
    }
    this.payment.billingAddress.email = this.shippingAddress.email;
    this.payment.billingAddress.firstName = this.shippingAddress.firstName;
    this.payment.billingAddress.lastName = this.shippingAddress.lastName;
    this.payment.billingAddress.streetAddress = this.shippingAddress.streetAddress;
    this.payment.billingAddress.aptSuite = this.shippingAddress.aptSuite;
    this.payment.billingAddress.city = this.shippingAddress.city;
    this.payment.billingAddress.stateProvince = this.shippingAddress.stateProvince;
    this.payment.billingAddress.postalCode = this.shippingAddress.postalCode;
    this.payment.billingAddress.phoneNumber = this.shippingAddress.phoneNumber;
    this.payment.billingAmount = this.getTotal();
    this.setAddresses(this.payment.billingAddress, this.shippingAddress);
  };

  this.setSuggestedAddress = function (suggestedAddress) {
    this.suggestedAddress.resultCodes = suggestedAddress.resultCodes;
    this.suggestedAddress.streetAddress = suggestedAddress.streetAddress;
    this.suggestedAddress.aptSuite = suggestedAddress.aptSuite;
    this.suggestedAddress.city = suggestedAddress.city;
    this.suggestedAddress.postalCode = suggestedAddress.postalCode;
    this.suggestedAddress.stateProvince = suggestedAddress.stateProvince;
    this.updateStorage();
  };

  this.setPayPal = function (paymentDetails, paymentAddress) {
    this.payment = {
      paymentType: ContentStore.getEnum('PaymentType').code.PAYPAL,
      payerEmail: paymentDetails.details.email,
      paymentMethodNonce: paymentDetails.nonce,
      payPal: {
        hasToken: !!paymentDetails.nonce,
        payerId: paymentDetails.details.payerId,
        payerEmail: paymentDetails.details.email
      },
      clientTokenHash: this.payment.clientTokenHash,
      billingAddress: this.payment.billingAddress,
      retryOrderPK: this.payment.retryOrderPK,
      msgSubId: this.payment.msgSubId
    };

    //Don't override shipping address that was already provided
    if (paymentAddress) {
      if (this.hasShippingAddress()) {
        const shippingAddress = this.parsePaypalAddress(paymentAddress);
        this.setShippingAddress(shippingAddress);
      } else {
        const billingAddress = this.parsePaypalAddress(paymentAddress);
        this.setAddresses(billingAddress, {});
      }
    }
    this.updateStorage();
  };

  this.setApplePayAddress = (paymentAddress) => {
    if (!paymentAddress) {
      return;
    }
    if (this.hasShippingAddress()) {
      this.setShippingAddress(paymentAddress);
      this.setBillingFromShipping();
    } else {
      if (paymentAddress.stateProvince && !paymentAddress.stateProvince.name) {
        paymentAddress.stateProvince = { name: paymentAddress.stateProvince };
      }
      this.setAddresses(paymentAddress, {});
    }
  };

  this.setApplePay = (paymentPayload, paymentAddress) => {
    this.payment = {
      paymentType: ContentStore.getEnum('PaymentType').code.APPLE_PAY,
      cardNumberDisplay: paymentPayload.details.paymentInstrumentName,
      cardType: paymentPayload.details.cardType,
      paymentMethodNonce: paymentPayload.nonce,
      applePay: {
        hasToken: !!paymentPayload.nonce
      },
      clientTokenHash: this.payment.clientTokenHash,
      billingAddress: this.payment.billingAddress,
      retryOrderPK: this.payment.retryOrderPK,
      msgSubId: this.payment.msgSubId
    };

    //If we didn't request address we don't need to update it
    this.setApplePayAddress(paymentAddress);
    this.updateStorage();
  };

  this.setGooglePay = function (paymentPayload, paymentAddress) {
    this.payment = {
      paymentType: ContentStore.getEnum('PaymentType').code.GOOGLE_PAY,
      cardNumberDisplay: `${paymentPayload.details.cardType} **** ${paymentPayload.details.lastFour}`,
      cardType: paymentPayload.details.cardType,
      paymentMethodNonce: paymentPayload.nonce,
      googlePay: {
        hasToken: !!paymentPayload.nonce
      },
      clientTokenHash: this.payment.clientTokenHash,
      billingAddress: this.payment.billingAddress,
      retryOrderPK: this.payment.retryOrderPK,
      msgSubId: this.payment.msgSubId
    };

    //If we didn't request address we don't need to update it
    if (paymentAddress) {
      if (this.hasShippingAddress()) {
        const shippingAddress = this.parseGooglePayAddress(paymentAddress);
        this.setShippingAddress(shippingAddress);
      } else {
        const billingAddress = this.parseGooglePayAddress(paymentAddress);
        this.setAddresses(billingAddress, {});
      }
    }
    this.updateStorage();
  };

  const normalizeCase = (value) => value?.trim()?.toUpperCase();

  this.areJobsUniform = () => {
    if (!subjects?.length) return false;

    const jobs = subjects.map((subject) => JobStore.getJob(subject?.portraitDashboard?.jobKey));

    if (jobs.some((job) => !job)) return false;

    if (jobs.length === 1) return true;

    const firstJob = jobs[0];

    return jobs.every(
      (job) =>
        normalizeCase(job.jobName) === normalizeCase(firstJob.jobName) &&
        job.schoolYearDisplay === firstJob.schoolYearDisplay &&
        normalizeCase(job.sellingMethod) === normalizeCase(firstJob.sellingMethod)
    );
  };

  this.parsePaypalAddress = function (paymentDetails) {
    const shippingAddress = paymentDetails.city
      ? {
          email: paymentDetails.email,
          firstName: paymentDetails.firstName,
          lastName: paymentDetails.lastName,
          streetAddress: paymentDetails.line1 || paymentDetails.streetAddress || paymentDetails.street1,
          aptSuite: paymentDetails.aptSuite || paymentDetails.street2 || '',
          city: paymentDetails.city,
          postalCode: paymentDetails.postalCode,
          phoneNumber: paymentDetails.phoneNumber || paymentDetails.contactPhone,
          stateProvince: paymentDetails.state || paymentDetails.stateProvince,
          country: paymentDetails.country || paymentDetails.countryCode
        }
      : {};
    return shippingAddress;
  };

  this.isRestoredItemsAvailable = function () {
    const types = ['packages', 'addOns', 'bundles', 'upgrades'];
    return subjects?.some((subject) =>
      types.some((type) =>
        subject[type]?.some((item) => item.orderLineDetails?.some((detail) => detail.restored === true))
      )
    );
  };

  this.parseGooglePayAddress = function (address) {
    if (!address?.postalCode) {
      return {};
    }
    return {
      email: address.email,
      streetAddress: address.address1,
      aptSuite: address.address2,
      city: address.locality,
      postalCode: address.postalCode,
      phoneNumber: address.phoneNumber?.replace(/[^\w]/gi, ''), //google pay returns special characters in phone number
      stateProvince: address.administrativeArea,
      country: address.countryCode
    };
  };

  this.setVenmo = function (paymentDetails) {
    this.payment = {
      paymentType: ContentStore.getEnum('PaymentType').code.VENMO,
      paymentMethodNonce: paymentDetails.nonce,
      venmo: {
        email: paymentDetails.details.payerInfo.email,
        firstName: paymentDetails.details.payerInfo.firstName,
        lastName: paymentDetails.details.payerInfo.lastName,
        userName: paymentDetails.details.username
      },
      clientTokenHash: this.payment.clientTokenHash,
      billingAddress: this.payment.billingAddress,
      retryOrderPK: this.payment.retryOrderPK,
      msgSubId: this.payment.msgSubId
    };
    this.updateStorage();
  };

  this.getOrderIds = function () {
    return R.map(R.prop('orderId'))(subjects);
  };

  this.getPaymentVoucherIds = function () {
    return R.map(R.prop('paymentVoucherId'))(subjects);
  };

  this.hasDigitalItem = function () {
    return subjects.some((subject) => subject.hasDigitalItem());
  };

  this.hasNonDigitalItem = function () {
    return subjects.some((subject) => subject.hasNonDigitalItem());
  };

  this.hasLongTailItem = function () {
    return this.getCurrentSubject().hasLongTailItem();
  };

  this.buildPayPalOrder = function () {
    const jobContext = JobStore.getJob();

    return {
      itemTotal:
        R.reduce((a, b) => a + b.getSubTotal() - b.getPromotionAmount(), 0)(subjects) -
        this.getCouponsTotalPromotionAmount() +
        this.getTotalFeesAmount(),
      tax: R.reduce((a, b) => a + b.taxAmount, 0)(subjects),
      shipping: R.reduce((a, b) => a + b.getShippingUnitPrice(), 0)(subjects),
      currency: jobContext.currency,
      shippingAddress: this.hasShippingAddress()
        ? {
            name:
              this.shippingAddress.firstName +
              (!!this.shippingAddress.lastName ? ` ${this.shippingAddress.lastName}` : ''),
            street1: this.shippingAddress.streetAddress,
            street2: this.shippingAddress.aptSuite,
            city: this.shippingAddress.city,
            postalCode: this.shippingAddress.postalCode,
            phone: this.shippingAddress.phoneNumber,
            stateProv: this.shippingAddress.stateProvince.name,
            country: this.shippingAddress.stateProvince.country || this.shippingAddress.country
          }
        : null
    };
  };

  this.updateOrderItemTotalAmount = function () {
    R.forEach((subject) => {
      subject.totalAmount = subject.orderTotalAmount;
    })(this.getSubjects());
  };

  this.submitPayment = async function () {
    if (this.isPaymentUsingPayPal()) {
      this.payment.payPal.payPalOrder = this.buildPayPalOrder();
    }

    const subjects = this.getSubjects();

    subjects.forEach((subject) => {
      subject.portraitDashboard.lifetouchUserProfileId = UserProfileStore.userProfile.lifetouchUserProfileId;
      subject.portraitDashboard.email = this.getReceiverEmailAddress();

      if (JobStore.isPayOnly(subject.portraitDashboard.jobKey) && subject.orderTotalAmount) {
        subject.totalAmount = subject.orderTotalAmount;
      }
    });

    const subjectList = this.buildSubjectListForSubmission(true);

    const cartResponse = await $http.post('cart/processPayment', {
      orders: subjectList,
      payment: this.payment,
      userProfile: UserProfileStore.userProfile,
      isGuestProfile: !UserProfileStore.isLoggedIn(),
      languageCode: TranslateProviderService.getLocale(),
      stateProvince: getStateProvince(),
      shippingAddress: this.shippingAddress,
      taxTotal: this.getTotalTaxAmount(),
      promoCodes: this.getPromoCodes(),
      coupons: this.getCoupons(),
      // processPayment reprices the cart server-side to validate the total; send the same pricing
      // context the earlier reprices used so a family-discount cart re-prices identically and does
      // not falsely trip the tamper check.
      context: { applyFamilyDiscountCoupon: this.familyDiscountActivated }
    });

    const { data } = cartResponse;

    if (data?.success) {
      const paymentResponse = data.payment?.paymentServiceResponse;

      this.payment.paymentServiceResponse = paymentResponse;
      this.payment.paypal = data.payment?.paypal;
      this.orderOpen = false;
      this.checkForLoyaltyUser = data.CheckForEnrollFlag;

      subjects.forEach((subject, orderIndex) => {
        const order = paymentResponse?.orders?.[orderIndex];

        subject.paymentVoucherId = order?.paymentVoucherId || paymentResponse?.paymentReceiptCode;

        subject.portraitDashboard.id = data.portraitDashboardIds?.[orderIndex];
        subject.subjectFirstName = order?.subjectFirstName;
      });

      if (data.userProfile) {
        UserProfileStore.setUserProfile(data.userProfile);
      }

      this.updateStorage();
    } else {
      if (data?.payment) {
        this.payment.retryOrderPK = data.payment.retryOrderPK;
        this.payment.msgSubId = data.payment.msgSubId;
      }

      if (data?.portraitDashboardIds) {
        subjects.forEach((subject, orderIndex) => {
          subject.portraitDashboard.id = data.portraitDashboardIds[orderIndex];
        });
      }

      // A gateway attempt spends the nonce. A missing token-hash/nonce response
      // likewise cannot be retried using the cached authorization. Clear both cases
      // so the retry re-authorizes rather than replaying invalid payment state.
      // Kept last so clearPaymentAfterFailure persists retryOrderPK/msgSubId.
      if (
        data?.payment?.paymentServiceResponse?.isPaymentFailed ||
        data?.error?.code === 'CLIENT_TOKEN_HASH_OR_PAYMENT_NONCE_MISSING'
      ) {
        this.clearPaymentAfterFailure();
      }
    }

    return cartResponse;
  };

  this.getAllItemsSubTotal = function () {
    return this.getSubtotalAfterDiscounts() + this.getCouponsTotalPromotionAmount();
  };

  this.getSubtotalAfterDiscounts = () => this.getTotalAmount() - this.getShippingUnitPrice();

  this.getTotalAmount = function () {
    return this.totalAmount;
  };

  this.getRewardId = function (rewards, code) {
    let rv = null;
    if (rewards) {
      const active = rewards.active;
      angular.forEach(active, (activeReward) => {
        if (activeReward.couponCode === code) {
          rv = activeReward.rewardId;
        }
      });
    }
    return rv;
  };

  this.getPromoCodes = function () {
    let promoCodes = [];
    if (!!ProductCatalogStore.getProductCatalog() && !!ProductCatalogStore.getProductCatalog().promotions) {
      promoCodes = R.pluck('promotionCode')(ProductCatalogStore.getProductCatalog().promotions);
    }
    return promoCodes;
  };

  this.getTotalTaxAmount = function () {
    return subjects?.reduce((acc, subject) => acc + subject.taxAmount, 0);
  };

  this.getTotal = function () {
    return this.getTotalAmount() + this.getTotalFeesAmount() + this.getTotalTaxAmount();
  };

  this.getTotalWithoutTax = function () {
    return this.getTotalAmount() + this.getTotalFeesAmount();
  };

  this.getRepriceVersion = function () {
    return this.repriceVersion;
  };

  this.updateSubjectsTotalAmount = function () {
    R.forEach((subject) => {
      subject.totalAmount = subject.getTotal();
    }, subjects);
  };

  this.getShippingPrice = function () {
    return R.sum(R.map((subject) => subject.getShippingPrice())(subjects));
  };

  this.getShippingUnitPrice = function () {
    const shippingUnitPrice = (subjects || []).reduce((acc, subject) => acc + subject.getShippingUnitPrice(), 0);

    return roundToDecimals(shippingUnitPrice, 2);
  };

  this.repriceShippableSubjectsOnOrder = function () {
    R.forEachIndexed((num, idx, list) => {
      if (this.hasShipping()) {
        this.repriceCart(idx);
      }
    }, subjects);
  };

  this.hasLastItem = function (orderIndex) {
    const subject = this.getSubjectOrder(orderIndex);
    return R.add(subject.addOns.length, subject.packages.length) === 1;
  };

  this.removeEmptySubjects = function () {
    subjects = R.filter((subject) => subject.subjectFirstName || subject.subjectDetailsDeferred, subjects);
  };

  this.isOrderOpen = function () {
    return this.orderOpen;
  };

  this.getSubjectOrderFirstNames = function () {
    return R.pluck('subjectFirstName')(subjects);
  };

  this.getTotalSubjectCount = function () {
    return subjects.length;
  };

  this.hasShipping = function () {
    return this.getSubjects().some((subject) => subject.getShippingUnitPrice() > 0);
  };

  this.hasShippingAddress = function () {
    return R.any((subject) => !!subject.shippingAddressRequired)(this.getSubjects());
  };

  this.hasShipOrdersToSubject = function () {
    const subjects = this.getAllSubjects();
    if (subjects.length === 0) {
      return false;
    }
    return subjects.some((subject) => {
      const jobContext = JobStore.getJobBySubject(subject);
      if (!jobContext) {
        return false;
      }
      return jobContext.shipOrdersTo === 'Subject';
    });
  };

  this.hasShipToHome = function () {
    const subjects = this.getAllSubjects();
    if (subjects.length === 0) {
      return false;
    }
    if (this.getNumberOfItemsInCartAndEnhancements() === 0) {
      return subjects.some((subject) => {
        const jobContext = JobStore.getJobBySubject(subject);
        if (!jobContext) {
          return false;
        }
        return jobContext.shipToHome === true;
      });
    }
    return subjects.some((subject) => ShippingTypeService.getDeliveryType(subject) === 'SHIPS_TO_HOME');
  };

  this.confirmSubjectAccount = function () {
    this.getCurrentSubject().subjectAccountConfirmed = true;
    this.updateStorage();
  };

  this.isSubjectAccountConfirmed = function () {
    if (this.getCurrentSubject()) {
      return this.getCurrentSubject().subjectAccountConfirmed;
    }
    return false;
  };

  this.getCouponPromotionAmount = function (couponIndex) {
    const couponPromotions = coupons[couponIndex]?.promotions ?? null;
    const couponCode = couponPromotions?.[0]?.couponCode;
    const promotionAmounts = [];

    const getPromotionSum = (promotions) =>
      promotions?.reduce((sum, promo) => sum + (promo.couponCode === couponCode ? promo.amount : 0), 0) ?? 0;

    subjects.forEach((subject) => {
      if (couponPromotions && subject.orderPromotion) {
        promotionAmounts.push(getPromotionSum(subject.orderPromotion));
      }

      const shippingPromotion = subject.shipping?.orderable?.promotions?.filter(
        (promo) => !this.isAutoApplyCoupon(promo)
      );
      if (shippingPromotion && couponPromotions) {
        promotionAmounts.push(getPromotionSum(shippingPromotion));
      }
    });

    if (promotionAmounts.length) {
      promotionAmounts.push(this.getOrderLineCouponPromotionAmount(coupons[couponIndex]));
    }

    return promotionAmounts.length ? promotionAmounts.reduce((a, b) => a + b, 0) : 0;
  };

  this.getOrderLineCouponPromotionAmount = function (coupon) {
    const couponCode = coupon.couponCode;

    if (isAutoApplyCoupon(coupon)) {
      return 0;
    }
    const orderLinePromotionAmount = [];
    let orderLines;
    let orderPromotion;
    R.forEach((subject) => {
      orderLines = subject.getOrderLines();
      R.forEach(
        ((orderLine) => {
          if (!!orderLine.orderPromotion) {
            orderPromotion = R.find(R.propEq('couponCode', couponCode))(orderLine.orderPromotion);

            if (!!orderPromotion) {
              orderLinePromotionAmount.push(orderPromotion.amount);
            }
          }
        }).bind(orderLinePromotionAmount)
      )(orderLines);
    })(subjects);
    return !R.isEmpty(orderLinePromotionAmount) ? R.sum(orderLinePromotionAmount) : 0;
  };

  this.getCouponsTotalPromotionAmount = function () {
    const couponTotalAmount = [];
    if (!!coupons && !R.isEmpty(coupons)) {
      R.forEachIndexed((coupon, couponIndex) => {
        couponTotalAmount.push(this.getCouponPromotionAmount(couponIndex));
      }, coupons);
    } else {
      couponTotalAmount.push(0);
    }
    return R.sum(couponTotalAmount);
  };

  this.getInstantSavings = function (orderIndex) {
    return this.getOrderPromotionAmount(orderIndex);
  };

  this.getTotalInstantSavings = function () {
    return R.sum(R.mapIndexed((subject, index) => this.getInstantSavings(index))(subjects));
  };

  this.addOrUpdateCoupon = function (couponCode, coupon) {
    const couponIndex = R.findIndex(R.propEq('couponCode', couponCode))(coupons);
    if (couponIndex >= 0) {
      //update coupon
      coupons[couponIndex] = coupon;
    } else {
      //add coupon
      coupons.push(coupon);
    }
  };

  this.getSuggestedProductsNotInCart = function (suggestedProducts) {
    const normalizedSuggestedProducts = Array.isArray(suggestedProducts) ? suggestedProducts : [];
    if (!activeSubject) {
      return normalizedSuggestedProducts;
    }

    const productsInPackages = [];
    R.forEach(function (pkg) {
      productsInPackages.push(
        R.map(function (item) {
          return item.item;
        })(pkg.orderLineDetails || [])
      );
    })(activeSubject.packages || []);
    const productsInCart = R.concat(
      R.flatten(productsInPackages),
      R.map(R.prop('orderable'))(activeSubject.addOns || [])
    );
    if (productsInCart.length === 0) {
      return normalizedSuggestedProducts;
    }

    const suggestedProductsNotInCart = R.filter(function (product) {
      return !R.find(R.propEq('skuCode', product))(productsInCart);
    }, normalizedSuggestedProducts);
    return suggestedProductsNotInCart;
  };

  this.currentSubjectHasProducts = function () {
    return activeSubject && this.getNumberOfItemsInCartAndEnhancements() > 0;
  };

  this.disableSuggestiveSellingForSubject = function (displayValue) {
    activeSubject.disableSuggestiveSelling = displayValue;
  };

  this.nonPayOnlyOrders = function () {
    return R.any((subject) => subject.jobContext.orderType !== 'PAYMENT')(subjects);
  };

  this.canAddMoreJobs = function () {
    if (this.getNumberOfSubjects() >= ContentStore.getConfig().maxJobs) {
      var modalInstance = $uibModal.open({
        animation: true,
        backdrop: 'static',
        windowClass: 'modal-sm',
        template:
          '<lt-confirm message="CONFIRM_TOO_MANY_CHILDREN_MESSAGE" button-text="CONFIRM_TOO_MANY_CHILDREN_DISMISS_BUTTON_TEXT" ' +
          'bottom-message="CONFIRM_TOO_MANY_CHILDREN_BOTTOM_MESSAGE" close="modalCtrl.dismiss()"></lt-confirm>',
        controller() {
          this.dismiss = function () {
            modalInstance.close();
          };
        },
        controllerAs: 'modalCtrl'
      });
      return false;
    }
    return true;
  };

  /**
   * Return the value of the current subjects nameOnText field.
   * @returns {*}
   */
  this.getCurrentSubjectNameOnText = function () {
    return this.getCurrentSubject().nameOnText;
  };

  /**
   * Set the value of the current subjects nameOnText field.
   * @param nameOnText
   */
  this.setCurrentSubjectNameOnText = function (nameOnText) {
    if (!!nameOnText) {
      this.getCurrentSubject().nameOnText = nameOnText;
    }
  };

  /**
   * Iterate over the packages in the current subject, if the subject contains a base package with upgrades
   * or bundle package return true, otherwise return false.
   * @returns {boolean}
   */
  this.subjectContainsStandardizedPricingBundles = function (subjectIndex) {
    const currentSubject = this.getSubjectOrder(subjectIndex);
    if (currentSubject) {
      return R.any((_package) => {
        if (_package && _package.orderable) {
          return (
            (_package.orderable.isBasePackage &&
              !!_package.orderable.upgrades &&
              _package.orderable.upgrades.length >= 1) ||
            _package.orderable.isBundlePackage
          );
        }
        return false;
      })(currentSubject.packages);
    }
    return false;
  };

  this.getTotalFeesAmount = function () {
    return R.sum(R.map((fee) => (!!fee && !!fee.amount ? fee.amount : 0.0))(this.getFees()));
  };

  this.getFees = function () {
    return this.fees;
  };

  this.loadFees = function () {
    this.fees = [];
    R.forEach((subject) => {
      R.forEach((fee) => {
        if (!!fee && !!fee.orderable) {
          const found = R.find((feeObj) => feeObj.chargeCode === fee.orderable.skuCode)(that.fees);
          if (found) {
            found.amount += fee.orderable.unitPrice;
          } else {
            that.fees.push({ chargeCode: fee.orderable.skuCode, amount: fee.orderable.unitPrice });
          }
        }
      })(subject.fees || []);
    })(subjects);

    return this.fees;
  };

  this.getSubjectByJobKey = function (jobKey) {
    const jobContext = JobStore.getJob(jobKey) || {};
    const subject = R.find((s) => s.portraitDashboard.jobKey === jobContext.jobKey)(subjects);
    return subject;
  };

  this.getSubjectByPictureDayData = (pictureDayData) =>
    subjects.find(
      (subject) =>
        (pictureDayData.portraitId === subject.portraitDashboard.portraitId &&
          pictureDayData.accessCode === subject.portraitDashboard.accessCode) ||
        pictureDayData.pictureDayId === subject.portraitDashboard.jobKey
    );

  this.setPaymentTimedOut = function () {
    this.paymentTimedOut = true;
  };

  this.setPaymentVoucherIds = function (ids) {
    this.paymentVoucherIds = ids;
  };

  this.deleteEmptyOrders = function () {
    const emptySubjectIds = subjects.filter((subject) => !this.hasOrderItems(subject)).map((subject) => subject.id);
    emptySubjectIds.forEach((id) => this.removeEmptySubjectById(id));
  };

  this.removeEmptySubjectById = function (id) {
    if (id && !this.getNumberOfItemsInOrderById(id)) {
      const subjectIndex = this.getSubjectIndex(id);
      const subjectToDelete = subjectIndex > -1 ? subjects[subjectIndex] : undefined;

      if (subjectToDelete) {
        // MLC-13293: removeSubject re-selects a subject after deleting one, but this path
        // did not, so sweeping the selected student left JobStore with no active job and
        // tripped RouterService's jobRequired guard on the way to /billing-info.
        const wasCurrent = subjectToDelete === this.getCurrentSubject();

        subjects.splice(subjectIndex, 1);
        this.removeJobContextWhenOrphan(subjectToDelete);
        if (wasCurrent && subjects.length > 0) {
          this.setCurrentSubject(0);
        }
        this.updateStorage();
      }
    }
  };

  this.getReceiverEmailAddress = function () {
    const shippingAddress = this.getShippingAddress();
    const billingAddress = this.getPayment().billingAddress;

    let receiverEmailAddress = null;
    if (shippingAddress?.streetAddress && shippingAddress.email) {
      receiverEmailAddress = shippingAddress.email;
    } else if (billingAddress?.streetAddress) {
      receiverEmailAddress = billingAddress.email;
    }

    return receiverEmailAddress || UserProfileStore.userProfile.email;
  };

  this.setPaymentMethodNonce = function (paymentMethodNonce) {
    this.payment.paymentMethodNonce = paymentMethodNonce;
    this.updateStorage();
  };

  // Discards the payment method after a gateway failure spent its single-use
  // nonce, forcing the next attempt to re-authorize. Keeps only what the retry
  // needs: the entered billing address (so the customer doesn't re-type it) and
  // the server's retry handle (retryOrderPK/msgSubId, set just before this call).
  this.clearPaymentAfterFailure = function () {
    this.payment = {
      billingAddress: this.payment.billingAddress,
      retryOrderPK: this.payment.retryOrderPK,
      msgSubId: this.payment.msgSubId
    };
    // PaymentService refreshes Braintree authorization only when this cache is empty.
    // The next payment attempt needs a token/hash pair that matches the new nonce.
    this.setClientAuthorization();
    this.updateStorage();
  };

  //move into a service
  this.getRegularCouponCount = () => this.getRegularCouponCountFrom(coupons);
  this.getRegularCouponCountFrom = (couponsList) =>
    couponsList.filter((coupon) => !this.isAutoApplyCoupon(coupon)).length;
  this.isAutoApplyCoupon = (coupon) => isAutoApplyCoupon(coupon);

  this.refreshSubject = (orderIndex) => {
    this._doRefreshSubject(orderIndex);

    this.updateStorage();
  };

  this._doRefreshSubject = (orderIndex) => {
    const subject = this.getSubjectOrder(orderIndex);

    if (subject) {
      subject.unlockedPremiumBgs = this._getUnlockedPremiumBgs(subject);
      subject.premiumBgAddons = this._reLockPremiumBgs(subject).map((bg) => this._toPremiumBgAddon(bg));

      this._adjustUpgrades(orderIndex, subject);

      subject.firstDigitalDownloads = DigitalDownloadService.getSubjectFirstDigitalDownloads(subject);
      subject.hasPackagePersonalization = this.subjectHasPackagePersonalization(subject);
    }
  };

  this._getUnlockedPremiumBgs = (subject) =>
    new Set(
      subject.getOrderLines().flatMap((orderLine) => {
        const orderable = orderLine.orderable;
        const productSkuCode = orderable.basePackage || orderable.skuCode;

        return orderLine.orderLineDetails
          .map((orderLineDetail) => orderLineDetail?.look?.background)
          .filter((bg) => bg?.premiumUpgrade?.unlockedBy?.has(productSkuCode))
          .map((bg) => bg.backgroundImageId);
      })
    );

  this._reLockPremiumBgs = (subject) => {
    const defaultLook = LooksStore.getDefaultLook(subject.portraitDashboard.jobKey);

    const [unlockedBgs, lockedBgs] = partitionArray(subject.getPremiumBackgrounds(), (bg) => {
      const unlockedBy = bg.premiumUpgrade.unlockedBy;

      return Boolean(!unlockedBy || subject.unlockedPremiumBgs.has(bg.backgroundImageId));
    });

    lockedBgs.forEach((bg) => {
      subject.resetPremiumBackgroundToDefault(bg.backgroundImageId, defaultLook);
    });

    return unlockedBgs;
  };

  this._toPremiumBgAddon = (bg) => {
    const premiumBgUpgrade = {
      ...bg.premiumUpgrade,
      backgroundImageId: bg.backgroundImageId
    };

    const premiumBgAddon = OrderLineFactory.createFromAddOn(premiumBgUpgrade, bg.look);
    premiumBgAddon.salesPrice = premiumBgUpgrade.salePrice;
    return premiumBgAddon;
  };

  this._adjustUpgrades = function (orderIndex, subject) {
    const availableEnhancements = this.getAvailableEnhancements(null, orderIndex);
    const skuCodes = R.map(R.prop('skuCode'))(availableEnhancements);
    subject.upgrades = R.filter((e) => R.contains(e.orderable?.skuCode)(skuCodes))(subject.upgrades);
  };

  this.addFreeGiftToCart = function (context) {
    if (!context) return;
    const CartRewardTrackerService = $injector.get('CartRewardTrackerService');
    const AddOnService = $injector.get('AddOnService');
    if (!CartRewardTrackerService.isEnabled()) return;
    if (this.hasFreeGift()) return;

    const { freeGiftCoupon } = context;

    const freeGiftSubjects = this._getFreeGiftSubjects(freeGiftCoupon.giftSku);

    if (freeGiftSubjects.length === 0) {
      loggerService.error(`Illegal state: no candidate subject with free gift SKU ${freeGiftCoupon.giftSku} was found`);

      return;
    }

    const currentSubject = this.getCurrentSubject();

    const isCurrentSubject = (holder) => holder.subject === currentSubject;
    const hasOrderLines = (holder) => holder.subject.hasOrderLines();

    const freeGiftSubject =
      freeGiftSubjects.find((holder) => hasOrderLines(holder) && isCurrentSubject(holder)) ??
      freeGiftSubjects.find(hasOrderLines) ??
      freeGiftSubjects.find(isCurrentSubject) ??
      freeGiftSubjects[0];

    const freeGiftProduct = {
      ...freeGiftSubject.addon,
      freeGift: true
    };

    const orderLine = OrderLineFactory.createFromAddOn(freeGiftProduct, null);

    return this.addAddOnToSubjectBySubjectAndReprice(freeGiftSubject.subject, freeGiftProduct).finally(() => {
      AddOnService.confirmAdded(orderLine);
      $rootScope.$broadcast('shoppingCart:freeGiftAdded', freeGiftProduct);
    });
  };

  this.getFreeGiftAddOn = function () {
    return this.getSubjects()
      ?.map((subject) => subject.addOns)
      .filter(Boolean)
      .flat()
      .find((addOn) => addOn.orderable?.freeGift === true);
  };

  this._getFreeGiftSubjects = (giftSku) => {
    const freeGiftAddons = ProductCatalogStore.findAddOnsBySkuWithJobKey(giftSku);

    return subjects
      .map((subject) => {
        const freeGiftAddon = freeGiftAddons.find((addon) => subject.portraitDashboard.jobKey === addon.jobKey);

        if (freeGiftAddon) {
          return {
            subject,
            ...freeGiftAddon
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  ResetJobAction.register(this.clearAll.bind(this));
  EmptyCartAction.register(this.clearAll.bind(this));
  SessionTimeoutAction.register(this.clearAll.bind(this, true));

  JobLoadedAction.register(this.onJobLoaded.bind(this));
  DeserializeAction.register(this.onDeserialized.bind(this));
}

App.service('ShoppingCartStore', [
  '$timeout',
  'ProductCatalogStore',
  'JobStore',
  'UserProfileStore',
  'LogoutSuccessAction',
  'JobLoadedAction',
  'DeserializeAction',
  'SessionTimeoutAction',
  'ResetJobAction',
  'EmptyCartAction',
  'OrderLineFactory',
  'StorageService',
  'TranslateProviderService',
  '$http',
  '$q',
  'ShoppingCartConstants',
  'GuidGenerator',
  'ContentStore',
  'AnalyticsService',
  '$uibModal',
  'ErrorLogService',
  'StandardizedEnhancementService',
  'SubjectStore',
  'FlightRecorderService',
  'PersistentCartService',
  'ShippingTypeService',
  'EnhancementStore',
  '$rootScope',
  '$injector',
  'loggerService',
  'FeatureFlagService',
  'DigitalDownloadService',
  'LooksStore',
  ShoppingCartStore
]);

App.run([
  'ShoppingCartStore',
  function (ShoppingCartStore) {
    // eagerly initialize the store so that event listeners are registered
  }
]);
